import { cloneElement, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getApi, postApi, postApiNoHeader, postApiStream } from 'redux/apis';
import { bumpConversations, clearConversationUnread, clearPendingMessage, clearPendingNewChat, endRunningConversation, notifyDataChanged, setActiveConversation, startRunningConversation } from 'redux/slice/chatBubbleSlice';
import { applyEmployeeCostFieldValue } from 'redux/slice/costAllocationSlice';
import { updateTransactionLineItemAction } from 'redux/action/transactionAction';
import { AppDispatch } from 'redux/store';
import { toastText } from 'utils/utils';
import { requestChatImport } from 'utils/pendingChatImport';
import styles from './index.module.scss';
import Hero from 'components/Chat/ChatSurface/Hero';
import ThreadOutline from 'components/Chat/ChatSurface/ThreadOutline';
import useSpeechInput from './useSpeechInput';
import CapMark from './CapMark';
import CapWordmark from './CapWordmark';
import SkillsManager, { Skill } from './SkillsManager';
import { useSmartImportChat } from './SmartImportChat/useSmartImportChat';
import SmartImportCard from './SmartImportChat/SmartImportCard';
import type { SmartImportStepKind } from './SmartImportChat/types';
import { useDirectAllocationChat } from './DirectAllocationChat/useDirectAllocationChat';
import DirectAllocationCard from './DirectAllocationChat/DirectAllocationCard';
import type { DirectAllocationStepKind } from './DirectAllocationChat/types';
import { useMappingRuleChat } from './MappingRuleChat/useMappingRuleChat';
import MappingRuleCard from './MappingRuleChat/MappingRuleCard';
import type { MappingRuleStepKind } from './MappingRuleChat/types';
import { useTimeRuleChat } from './TimeRuleChat/useTimeRuleChat';
import TimeRuleCard from './TimeRuleChat/TimeRuleCard';
import type { TimeRuleStepKind, TimeRuleType } from './TimeRuleChat/types';
import JournalDraftReview from 'components/JournalDrafts/JournalDraftReview';
import type { PostResult } from 'components/JournalDrafts/types';

// Exact mic glyph from the source design file (CAP Dashboard AI.dc.html, `micB` control).
const MicIcon = () => (
	<svg
		width="17"
		height="17"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.9"
		strokeLinecap="round"
	>
		<rect x="9" y="2.6" width="6" height="11" rx="3" />
		<path d="M5.5 11.4a6.5 6.5 0 0 0 13 0" />
		<path d="M12 17.9V21.4" />
	</svg>
);

// "Dock to corner" glyph used for the reset-position control.
const ResetPositionIcon = () => (
	<svg
		width="14"
		height="14"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.9"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<rect x="3" y="3" width="18" height="18" rx="3" />
		<rect x="13" y="13" width="8" height="8" rx="1.5" fill="currentColor" stroke="none" />
	</svg>
);

// Paperclip glyph for the image-attachment control.
const AttachIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.9"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<path d="M18.5 8.5 10 17a3.5 3.5 0 0 1-5-5l8-8a2.5 2.5 0 0 1 3.5 3.5l-7.5 7.5a1 1 0 0 1-1.5-1.5l6.5-6.5" />
	</svg>
);

// Plain document glyph — shown in the attachment row for a CSV/XLSX file,
// which (unlike an image) has no thumbnail to render.
const FileIcon = () => (
	<svg
		width="18"
		height="18"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.7"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<path d="M13.5 3H7a1.5 1.5 0 0 0-1.5 1.5v15A1.5 1.5 0 0 0 7 21h10a1.5 1.5 0 0 0 1.5-1.5V8L13.5 3Z" />
		<path d="M13 3v5h5" />
	</svg>
);

// Expand-arrows glyph for the maximize control.
const MaximizeIcon = () => (
	<svg
		width="14"
		height="14"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.9"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<path d="M9 4H4v5" />
		<path d="M15 4h5v5" />
		<path d="M9 20H4v-5" />
		<path d="M15 20h5v-5" />
	</svg>
);

// Collapse-arrows glyph for the restore (un-maximize) control.
const RestoreIcon = () => (
	<svg
		width="14"
		height="14"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.9"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<path d="M4 9h5V4" />
		<path d="M20 9h-5V4" />
		<path d="M4 15h5v5" />
		<path d="M20 15h-5v5" />
	</svg>
);

// Pencil glyph for editing a question you've already sent.
const EditIcon = () => (
	<svg
		width="12"
		height="12"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<path d="M12 20h9" />
		<path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
	</svg>
);

// Filled square — the conventional "stop generating" affordance, shown in
// place of send while a reply is in flight.
const CopyIcon = () => (
	<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
		<rect x="9" y="9" width="11" height="11" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.7" />
		<path d="M5.5 15H5a1 1 0 01-1-1V5a1 1 0 011-1h9a1 1 0 011 1v.5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
	</svg>
);

const CheckSmallIcon = () => (
	<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
		<path d="M5 12.5l4.5 4.5L19 7" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

const ArrowDownIcon = () => (
	<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
		<path d="M12 5v14m-6-6l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

// Wall-clock time for a message, in the viewer's own locale.
const formatMessageTime = (timestamp?: number): string =>
	timestamp
		? new Date(timestamp).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
		: '';

const StopIcon = () => (
	<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
		<rect x="6" y="6" width="12" height="12" rx="2" />
	</svg>
);

// Mirrors the discriminated union returned by aiAssistantActionsService on
// the backend — the assistant can propose one of these, but nothing is
// written until the user clicks Confirm on the resulting card.
interface AddEmployeeProposal {
	type: 'add_employee';
	companyId: string;
	fullName: string;
	payPeriodId?: string;
	payPeriodLabel?: string;
	summary: string;
}

// Mirrors aiAssistantActionsService's AddGlMappingRuleProposal. Carries no
// `actions` blob — that is a full clone of the company's configuration and is
// rebuilt server-side at confirm time; the card only needs the routes to show.
interface AddGlMappingRuleProposal {
	type: 'add_gl_mapping_rule';
	companyId: string;
	payPeriodId: string;
	payPeriodLabel: string;
	name: string;
	description?: string;
	criteria: unknown;
	employeeNames: string[];
	overrides: {
		sectionNo: string;
		jsonId: string;
		sectionName: string;
		fieldName: string;
		accountQboId: string;
		accountName: string;
		defaultAccountName: string;
	}[];
	shadowedBy?: { name: string; priority: number };
	summary: string;
}

interface UpdateEmployeeCostProposal {
	type: 'update_employee_cost';
	companyId: string;
	employeeId: string;
	employeeName: string;
	fieldId: string;
	fieldName: string;
	configurationSectionId: string;
	payPeriodId: string;
	payPeriodLabel: string;
	previousValue: string;
	newValue: string;
	summary: string;
}

// The bulk sibling of UpdateEmployeeCostProposal. Carries counts (not a list
// of rows) so the confirmation card can say how many employees it touches;
// the backend re-resolves the actual scope at confirm time.
interface BulkUpdateEmployeeCostProposal {
	type: 'bulk_update_employee_cost';
	companyId: string;
	fieldId: string;
	fieldName: string;
	configurationSectionId: string;
	payPeriodId: string;
	payPeriodLabel: string;
	changeType: 'set' | 'increase' | 'decrease';
	amount: string;
	employeeIds?: string[];
	scopeLabel: string;
	affectedCount: number;
	willCreateCount: number;
	summary: string;
}

interface UpdateExpenseLineProposal {
	type: 'update_expense_line';
	companyId: string;
	txnId: string;
	lineId: string;
	entityType: string;
	targetField: 'account' | 'customer' | 'class' | 'location';
	newValueId: string;
	newValueName: string;
	previousValueName: string;
	summary: string;
}

interface UpdateTimeActivityProposal {
	type: 'update_time_activity';
	companyId: string;
	timeActivityId: string;
	employeeName: string;
	activityDateLabel: string;
	payPeriodId?: string;
	targetField: 'hours' | 'customer' | 'class';
	newValue: string;
	newValueId?: string;
	previousValue: string;
	summary: string;
}

// Bulk customer/class recode across a period's time entries. Hours are not
// bulk-editable, so there is no changeType here.
interface BulkUpdateTimeActivityProposal {
	type: 'bulk_update_time_activity';
	companyId: string;
	payPeriodId: string;
	payPeriodLabel: string;
	employeeId?: string;
	employeeName?: string;
	targetField: 'customer' | 'class';
	newValueId: string;
	newValue: string;
	scopeLabel: string;
	affectedCount: number;
	summary: string;
}

// Mirrors the backend's FieldRef (smartImportInterface.ts) — which existing
// field, or which not-yet-created one, a value belongs to.
type ImportFieldRef = { type: 'existing'; fieldId: string } | { type: 'pending'; tempId: string };

interface ImportRowValue {
	fieldRef: ImportFieldRef;
	value: string;
	sourceHeader?: string;
}

interface ImportRow {
	rowId: string;
	employeeRef: { type: 'existing'; employeeId: string } | { type: 'new'; rowId: string };
	values: ImportRowValue[];
}

// Mirrors the backend's SmartImportRequest — round-tripped opaquely through
// Confirm, but the table (below) reads `rows` directly to render/edit cells.
interface ImportRequest {
	companyId: string;
	payPeriodId: string;
	pendingFields: unknown[];
	newEmployees: unknown[];
	reactivations: string[];
	rows: ImportRow[];
}

// One cost column of the import table — mirrors the backend's
// ImportFieldSummary (aiImportEmployeeCostService.ts).
interface ImportFieldSummary {
	key: string;
	label: string;
	fieldRef: ImportFieldRef;
}

// One row of the import table — mirrors the backend's ImportRowDetail.
interface ImportRowDetail {
	rowId: string;
	employeeName: string;
	status: 'matched' | 'saved' | 'new';
	totalAmount: number;
}

// The proposal for an attached CSV/XLSX file (see ChatBubble's importFile
// attachment below) — `request` is the fully server-resolved Smart Import
// payload, echoed back verbatim on Confirm (and rewritten in place by an edit
// to the table below, via adjustImportCell).
interface ImportEmployeeCostProposal {
	type: 'import_employee_cost';
	companyId: string;
	payPeriodId: string;
	payPeriodLabel: string;
	fileName: string;
	request: ImportRequest;
	summary: string;
	// Also echoed back as `pendingImportContext` on a follow-up chat message
	// (see sendMessage) so the assistant can interpret "view all employee
	// records" or "apply $500 to everyone's salary" against this specific
	// import — and read directly here to render/edit the table.
	fields: ImportFieldSummary[];
	rowDetails: ImportRowDetail[];
	totalAmount: number;
	// Normally empty — every numeric column is now auto-resolved server-side
	// (an AI guess or a generic catch-all section), with no manual mapping
	// step in the UI. Only a company with zero configured cost sections at
	// all would still leave something here, with nowhere to place its values
	// (surfaced only in the card's summary text, not an interactive picker).
	unmappedColumns: Array<{
		colIndex: number;
		sourceHeader: string;
		valuesByRowId: Record<string, number>;
	}>;
	availableSections: Array<{ id: string; name: string }>;
}

// Mirrors aiAssistantActionsService's QboMcpWriteProposal — a change made in
// QuickBooks Online itself (through the QBO MCP middleware) once confirmed.
// The confirm response carries QuickBooks' own description of what it did,
// shown as the follow-up message instead of echoing the summary.
interface QboMcpWriteProposal {
	type: 'qbo_mcp_write';
	companyId: string;
	instruction: string;
	summary: string;
}

// Pull a pay period's time logs from QuickBooks again (Time Logs → Sync Now).
// Confirming only starts the sync; the usual websocket notification reports
// when it finishes.
interface SyncTimeActivityProposal {
	type: 'sync_time_activity';
	companyId: string;
	payPeriodId: string;
	payPeriodLabel: string;
	summary: string;
}

// The indirect expense rate for one pay period (Settings → Configurations).
interface UpdateIndirectRateProposal {
	type: 'update_indirect_rate';
	companyId: string;
	payPeriodId: string;
	payPeriodLabel: string;
	previousRate: number | null;
	newRate: number;
	journalPublished: boolean;
	summary: string;
}

// Mirrors aiAssistantAllocationLookup's FileDownload: a report the user asked
// for in chat. The Download button fetches it from the same export endpoint
// the Reports / Cost Allocations screens use, under the user's own session.
interface FileDownload {
	report: 'cost_allocation' | 'payroll_summary' | 'time_summary';
	format: 'csv' | 'pdf';
	label: string;
	query: Record<string, string>;
}

const REPORT_ENDPOINTS: Record<FileDownload['report'], { csv: string; pdf: string; fileName: string }> = {
	cost_allocation: { csv: '/cost-allocation/export-csv', pdf: '/cost-allocation/export-pdf', fileName: 'CostAllocation' },
	payroll_summary: { csv: '/reports/payroll-expense-summary-csv', pdf: '/reports/payroll-expense-summary-pdf', fileName: 'PayrollSummaryReport' },
	time_summary: { csv: '/reports/time-activity-summary-csv', pdf: '/reports/time-activity-summary-pdf', fileName: 'TimeSummaryReport' },
};

// Same file-saving steps as ExportData: CSV endpoints return the text, PDF
// endpoints return base64 in `data`.
const downloadReportFile = async (download: FileDownload) => {
	const endpoint = REPORT_ENDPOINTS[download.report];
	const response: any = await getApi(endpoint[download.format], download.query);
	let blob: Blob;
	if (download.format === 'pdf') {
		const bytes = atob(response.data.data);
		const buffer = new Uint8Array(bytes.length);
		for (let i = 0; i < bytes.length; i++) {
			buffer[i] = bytes.charCodeAt(i);
		}
		blob = new Blob([buffer], { type: 'application/pdf' });
	} else {
		blob = new Blob([response.data], { type: 'text/csv' });
	}
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = `${endpoint.fileName}_${new Date().toISOString().slice(0, 10)}.${download.format}`;
	link.style.display = 'none';
	document.body.appendChild(link);
	link.click();
	link.remove();
	URL.revokeObjectURL(url);
};

type DownloadStatus = 'ready' | 'downloading' | 'done' | 'error';

// Mirrors aiPayrollJournalService's CreatePayrollJournalProposal — the pay
// period's cost allocation journal, built by the same logic as Preview Journal
// and posted to QuickBooks (through the QBO MCP middleware) once confirmed.
// Read-only here: a different date or notes is asked for in the chat, which
// re-previews and re-signs the card.
interface PayrollJournalLine {
	account: string;
	debit: string;
	credit: string;
	employeeName: string;
	customer: string;
	class: string;
	location: string;
	ruleName: string | null;
}

interface CreatePayrollJournalProposal {
	type: 'create_payroll_journal';
	companyId: string;
	payPeriodId: string;
	payPeriodLabel: string;
	journalDate: string;
	notes: string;
	qboJournalNo: string;
	mode: 'create' | 'update';
	existingQboJournalTrnId: string | null;
	existingQboJournalNo: string | null;
	lines: PayrollJournalLine[];
	totalDebit: string;
	totalCredit: string;
	warnings: string[];
	linesHash: string;
	summary: string;
	// Set once the backend persists the proposal as a JournalDraft. The card
	// then renders JournalDraftReview and drives approve/edit/decline through
	// /journal-drafts — never /ai/actions/confirm. Absent on older
	// conversations, which keep the legacy confirm card below.
	draftId?: string;
	version?: number;
}

const isJournalDraftAction = (action: PendingAction | undefined): action is CreatePayrollJournalProposal & { draftId: string } =>
	!!action && action.type === 'create_payroll_journal' && typeof action.draftId === 'string' && action.draftId.length > 0;

type PendingAction = AddEmployeeProposal | AddGlMappingRuleProposal | UpdateEmployeeCostProposal | BulkUpdateEmployeeCostProposal | UpdateExpenseLineProposal | UpdateTimeActivityProposal | BulkUpdateTimeActivityProposal | ImportEmployeeCostProposal | QboMcpWriteProposal | CreatePayrollJournalProposal | SyncTimeActivityProposal | UpdateIndirectRateProposal;

const formatJournalAmount = (value: string) =>
	value ? `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '';

// YYYY-MM-DD → M/D/YYYY without a Date round-trip (which would shift the day
// in timezones west of UTC).
const formatJournalDate = (value: string) => {
	const [year, month, day] = value.split('-');
	return year && month && day ? `${Number(month)}/${Number(day)}/${year}` : value;
};

// 'superseded': an import card whose card was replaced by a newer one after a
// follow-up edit (e.g. "apply $500 to everyone") — see sendMessage's
// supersede-the-previous-card step. Distinct from 'cancelled' (the user
// explicitly declined) so the copy can say what actually happened.
type ActionStatus = 'pending' | 'confirming' | 'confirmed' | 'cancelled' | 'error' | 'superseded';

// Mirrors aiAssistantService.ts's PendingNavigation — an inferred (not
// explicitly requested) page match the backend isn't confident enough in to
// navigate to outright, so it asks first instead.
interface PendingNavigation {
	path: string;
	label: string;
}

type NavigationStatus = 'pending' | 'confirmed' | 'cancelled';

// Mirrors aiAssistantService.ts's DataCard — a successful read-tool lookup
// (get_pay_period_totals, get_expense_spend_by_customer_or_vendor, etc.)
// from this turn, rendered as a small stat card/table via renderDataCard
// instead of leaving those numbers buried in the prose reply.
interface DataCard {
	tool: string;
	data: any;
}

interface Message {
	role: 'user' | 'assistant';
	text: string;
	timestamp: number;
	imageUrl?: string;
	// Set on the user's own message bubble when it carried a CSV/XLSX
	// attachment, so the thread shows what was uploaded (there is no visual
	// preview for a spreadsheet the way there is for an image).
	importFileName?: string;
	// Set on the user's message bubble when they pinned a skill in the composer
	// for that message, so the thread shows which skill the assistant answered
	// under.
	skillName?: string;
	pendingAction?: PendingAction;
	actionStatus?: ActionStatus;
	pendingNavigation?: PendingNavigation;
	navigationStatus?: NavigationStatus;
	followUps?: string[];
	dataCard?: DataCard;
	fileDownload?: FileDownload;
	downloadStatus?: DownloadStatus;
	// Set on the placeholder turn appended when the user hits Stop. It renders
	// as an inline notice with a retry rather than a reply bubble, and is
	// filtered out of the history sent to the model — nothing was said.
	//
	// 'support' marks the escalation card offered after a long unresolved chat
	// (see the escalation section below) — an editable overview of the
	// conversation the user can send to the support team as an Outseta ticket.
	// Like 'stopped', it's a UI artifact and never goes to the model.
	variant?: 'stopped' | 'support';
	supportReason?: SupportReason;
	supportSubject?: string;
	supportSummary?: string;
	supportStatus?: SupportStatus;
	supportTicketRef?: string | null;
	// Marks this message as a Smart Import chat card (see SmartImportChat/). At
	// most one message carries this at a time — the flow freezes it (clearing the
	// field, overwriting `text` with a one-line summary) before appending the next
	// step's message, so only ever the newest is interactive.
	smartImportStep?: SmartImportStepKind;
	// Same convention as smartImportStep, for the Employee Direct Allocation chat
	// flow (see DirectAllocationChat/). The two are mutually exclusive per import
	// — starting one resets/freezes the other.
	directAllocationStep?: DirectAllocationStepKind;
	// Custom GL Mapping Rule builder step (see MappingRuleChat/) — its own
	// field for the same reason directAllocationStep is separate: freezing one
	// flow's active card must never touch another's.
	mappingRuleStep?: MappingRuleStepKind;
	// Time Rules handoff step (see TimeRuleChat/) — its own field so freezing
	// one flow's active card never touches another's.
	timeRuleStep?: TimeRuleStepKind;
	// Marks the "what would you like to do with this file?" chooser card shown
	// right after a CSV/XLSX attachment is sent — see pendingImportChoiceFileRef.
	importChoicePending?: boolean;
	// Set on the assistant bubble while its reply is streaming in from
	// /ai/chat/stream, so the thread can show a caret / "generating" affordance.
	// Cleared when the final result lands. streamKey uniquely identifies the
	// in-progress bubble so live deltas update exactly it (index-based lookups
	// race across async setMessages updates).
	streaming?: boolean;
	streamKey?: string;
}

// drafting: waiting on the assistant's overview. ready: the draft is editable
// and sendable. sending/sent: the Outseta ticket is in flight / filed.
// dismissed: the user chose to keep working in chat instead.
type SupportStatus = 'drafting' | 'ready' | 'sending' | 'sent' | 'dismissed';

// Why the card appeared. 'user_asked' comes from the user saying so outright
// (matched here or recognized by the model); the rest are the model's read of
// the conversation. It only changes the card's opening line — someone who just
// asked for a human doesn't need to be told the chat has gone on a while.
type SupportReason = 'user_asked' | 'repeated_question' | 'cant_help' | 'user_frustrated';

const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;

// Import Employee Cost via chat — a CSV/XLSX attachment instead of a
// screenshot. Extension-checked (not mimetype-checked) to match the backend's
// own check in helpers/multer.ts, since these mimetypes are unreliable across
// browsers/OSes. Kept in sync BY HAND with that file's
// MAX_CHAT_IMPORT_FILE_SIZE_BYTES / ALLOWED_CHAT_IMPORT_FILE_EXTENSIONS.
const ALLOWED_IMPORT_FILE_EXTENSIONS = ['csv', 'xlsx', 'xls'];
const MAX_IMPORT_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const ATTACH_ACCEPT = [...ALLOWED_IMAGE_TYPES, ...ALLOWED_IMPORT_FILE_EXTENSIONS.map((ext) => `.${ext}`)].join(',');

const fileExtension = (fileName: string): string => (fileName.split('.').pop() || '').toLowerCase();

// App routes the assistant is allowed to link to — kept in sync with the
// system prompt on the backend (aiAssistantService.ts) and the router
// (router/BrowserRouter.tsx). Matched against markdown-style links in
// assistant replies so they navigate in-app instead of rendering as plain
// text or a full-page anchor.
const APP_PAGE_PATHS = new Set([
	'/',
	'/employee-costs',
	'/time-activity',
	'/cost-allocations',
	'/expense-allocations',
	'/journal-entries',
	'/reports',
]);

const MARKDOWN_LINK_PATTERN = /\[([^\]]+)\]\((\/[a-zA-Z0-9\-_/]*)\)/g;

// The assistant's replies use lightweight markdown (**bold** for UI labels,
// `code` for exact field/value names) plus plain line breaks — this renders
// just those, without pulling in a full markdown library for a chat bubble
// that only ever needs a few marks.
const INLINE_MARK_PATTERN = /(\*\*[^*\n]+\*\*|`[^`\n]+`)/g;

function renderInlineFormatting(text: string, keyPrefix: string): React.ReactNode[] {
	const lines = text.split('\n');
	const nodes: React.ReactNode[] = [];
	lines.forEach((line, lineIndex) => {
		line.split(INLINE_MARK_PATTERN).filter(Boolean).forEach((part, partIndex) => {
			const partKey = `${keyPrefix}-${lineIndex}-${partIndex}`;
			if (part.startsWith('**') && part.endsWith('**') && part.length > 3) {
				nodes.push(<strong key={partKey}>{part.slice(2, -2)}</strong>);
			} else if (part.startsWith('`') && part.endsWith('`') && part.length > 1) {
				nodes.push(<code key={partKey}>{part.slice(1, -1)}</code>);
			} else {
				nodes.push(part);
			}
		});
		if (lineIndex < lines.length - 1) {
			nodes.push(<br key={`${keyPrefix}-br${lineIndex}`} />);
		}
	});
	return nodes;
}

// Splits assistant text on markdown-style links and turns any that match a
// real app route into an in-app navigation instead of plain or dead-anchor
// text — the backend's system prompt is instructed to use this exact syntax
// for the pages listed in APP_PAGE_PATHS. Remaining text segments still get
// **bold**/`code`/line-break formatting via renderInlineFormatting.
function renderMessageWithLinks(text: string, onNavigate: (path: string) => void) {
	const nodes: React.ReactNode[] = [];
	let lastIndex = 0;
	let match: RegExpExecArray | null;
	let key = 0;
	MARKDOWN_LINK_PATTERN.lastIndex = 0;
	while ((match = MARKDOWN_LINK_PATTERN.exec(text)) !== null) {
		const [full, label, path] = match;
		if (match.index > lastIndex) {
			nodes.push(...renderInlineFormatting(text.slice(lastIndex, match.index), `seg-${key}`));
		}
		if (APP_PAGE_PATHS.has(path)) {
			nodes.push(
				<a
					key={`link-${key++}`}
					className={styles['message-link']}
					href={path}
					onClick={(e) => {
						e.preventDefault();
						onNavigate(path);
					}}
				>
					{label}
				</a>
			);
		} else {
			nodes.push(...renderInlineFormatting(full, `full-${key++}`));
		}
		lastIndex = match.index + full.length;
	}
	if (lastIndex < text.length) {
		nodes.push(...renderInlineFormatting(text.slice(lastIndex), `tail-${key}`));
	}
	return nodes;
}

// ---- Block-level formatting for assistant replies ------------------------
//
// renderMessageWithLinks above only handles inline marks, which was enough
// while every reply was a few sentences. QuickBooks answers (and some CAP
// ones) come back as Markdown tables and record details, and printed raw they
// read as rows of pipes. This splits a reply into blocks and renders each one
// properly, still passing every piece of text through renderMessageWithLinks
// so bold, code and in-app links keep working inside cells and list items.
//
//   | a | b |  + separator row   → table
//   Label: value  (2+ lines)      → detail card
//   - item / 1. item              → list
//   # Heading                     → title
//   "Based on QuickBooks Online data:" / "Source: QuickBooks Online" → source badge

const TABLE_ROW_PATTERN = /^\s*\|.*\|\s*$/;
const TABLE_SEPARATOR_PATTERN = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;
const HEADING_PATTERN = /^\s*#{1,6}\s+(.+?)\s*#*\s*$/;
const BULLET_PATTERN = /^\s*[-*•]\s+(.*)$/;
const NUMBERED_PATTERN = /^\s*\d+[.)]\s+(.*)$/;
const RULE_PATTERN = /^\s*(-{3,}|\*{3,}|_{3,})\s*$/;
// "Customer: ABC Company", "**Invoice Amount:** $1,250", "- **Date**: Oct 4".
// The label is kept short and sentence-free so ordinary prose with a colon
// ("Note: this only covers…" is fine, "We found the following: …" is not a
// label) doesn't get boxed up as a card.
const DETAIL_PATTERN = /^\s*(?:[-*•]\s+)?(?:\*\*)?([A-Za-z][A-Za-z0-9 #/&()'.-]{0,38}?)(?:\*\*)?\s*:\s*(?:\*\*)?\s*(\S.*?)\s*$/;
const QBO_SOURCE_LINE_PATTERN = /^\s*[_*]*\s*(?:source:\s*quickbooks online|based on quickbooks online data:?)\s*[_*]*\s*(.*)$/i;
const NUMERIC_CELL_PATTERN = /^\s*[-+(]?\s*[$€£]?\s*[-+]?[\d,]+(\.\d+)?\s*%?\)?\s*$/;

type MessageBlock =
	| { kind: 'table'; header: string[]; rows: string[][]; align: ('left' | 'right')[] }
	| { kind: 'details'; items: { label: string; value: string }[] }
	| { kind: 'list'; ordered: boolean; items: string[] }
	| { kind: 'heading'; text: string }
	| { kind: 'source'; rest: string }
	| { kind: 'rule' }
	| { kind: 'paragraph'; text: string };

const splitTableRow = (line: string): string[] =>
	line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((cell) => cell.trim());

const detailOf = (line: string): { label: string; value: string } | null => {
	if (TABLE_ROW_PATTERN.test(line)) {
		return null;
	}
	const match = DETAIL_PATTERN.exec(line);
	if (!match) {
		return null;
	}
	const label = match[1].trim();
	// Times ("10:30") and URLs ("https://…") also contain a colon.
	if (/^\d+$/.test(label) || /^https?$/i.test(label) || match[2].startsWith('//')) {
		return null;
	}
	return { label, value: match[2].replace(/\*\*$/, '').trim() };
};

const startsBlock = (lines: string[], i: number): boolean => {
	const line = lines[i];
	return (
		(TABLE_ROW_PATTERN.test(line) && i + 1 < lines.length && TABLE_SEPARATOR_PATTERN.test(lines[i + 1])) ||
		HEADING_PATTERN.test(line) ||
		BULLET_PATTERN.test(line) ||
		NUMBERED_PATTERN.test(line) ||
		RULE_PATTERN.test(line) ||
		QBO_SOURCE_LINE_PATTERN.test(line) ||
		(detailOf(line) !== null && i + 1 < lines.length && detailOf(lines[i + 1]) !== null)
	);
};

function parseMessageBlocks(text: string): MessageBlock[] {
	const lines = text.replace(/\r\n/g, '\n').split('\n');
	const blocks: MessageBlock[] = [];
	let i = 0;

	while (i < lines.length) {
		const line = lines[i];

		if (!line.trim()) {
			i++;
			continue;
		}

		if (TABLE_ROW_PATTERN.test(line) && i + 1 < lines.length && TABLE_SEPARATOR_PATTERN.test(lines[i + 1])) {
			const header = splitTableRow(line);
			const separator = splitTableRow(lines[i + 1]);
			const rows: string[][] = [];
			i += 2;
			while (i < lines.length && TABLE_ROW_PATTERN.test(lines[i])) {
				const cells = splitTableRow(lines[i]);
				rows.push(header.map((_h, c) => cells[c] ?? ''));
				i++;
			}
			// Right-align a column when the separator says so (`---:`) or when
			// every value in it is a number/amount — so money lines up.
			const align = header.map((_h, c) => {
				const explicit = separator[c] ?? '';
				if (/-:$/.test(explicit)) {
					return 'right' as const;
				}
				const values = rows.map((r) => r[c].replace(/\*\*/g, '')).filter(Boolean);
				return values.length > 0 && values.every((v) => NUMERIC_CELL_PATTERN.test(v)) ? ('right' as const) : ('left' as const);
			});
			blocks.push({ kind: 'table', header, rows, align });
			continue;
		}

		const source = QBO_SOURCE_LINE_PATTERN.exec(line);
		if (source) {
			blocks.push({ kind: 'source', rest: source[1].replace(/^\*\*\s*/, '').trim() });
			i++;
			continue;
		}

		const heading = HEADING_PATTERN.exec(line);
		if (heading) {
			blocks.push({ kind: 'heading', text: heading[1] });
			i++;
			continue;
		}

		if (RULE_PATTERN.test(line)) {
			blocks.push({ kind: 'rule' });
			i++;
			continue;
		}

		// Checked before lists: "- **Customer:** ABC" lines are record details.
		if (detailOf(line) && i + 1 < lines.length && detailOf(lines[i + 1])) {
			const items: { label: string; value: string }[] = [];
			while (i < lines.length && detailOf(lines[i])) {
				items.push(detailOf(lines[i])!);
				i++;
			}
			blocks.push({ kind: 'details', items });
			continue;
		}

		if (BULLET_PATTERN.test(line) || NUMBERED_PATTERN.test(line)) {
			const ordered = NUMBERED_PATTERN.test(line);
			const pattern = ordered ? NUMBERED_PATTERN : BULLET_PATTERN;
			const items: string[] = [];
			while (i < lines.length && pattern.test(lines[i])) {
				items.push(pattern.exec(lines[i])![1]);
				i++;
			}
			blocks.push({ kind: 'list', ordered, items });
			continue;
		}

		const paragraph: string[] = [line];
		i++;
		while (i < lines.length && lines[i].trim() && !startsBlock(lines, i)) {
			paragraph.push(lines[i]);
			i++;
		}
		blocks.push({ kind: 'paragraph', text: paragraph.join('\n') });
	}

	return blocks;
}

function renderAssistantMessage(text: string, onNavigate: (path: string) => void): React.ReactNode {
	const blocks = parseMessageBlocks(text);
	// Plain prose (most CAP replies) renders exactly as before.
	if (blocks.every((block) => block.kind === 'paragraph')) {
		return renderMessageWithLinks(text, onNavigate);
	}

	return (
		<div className={styles['md']}>
			{blocks.map((block, b) => {
				const key = `md-${b}`;
				switch (block.kind) {
					case 'table':
						return (
							<div className={styles['md-table-wrap']} key={key}>
								<table className={styles['md-table']}>
									<thead>
										<tr>
											{block.header.map((cell, c) => (
												<th key={c} style={{ textAlign: block.align[c] }}>
													{renderMessageWithLinks(cell, onNavigate)}
												</th>
											))}
										</tr>
									</thead>
									<tbody>
										{block.rows.map((row, r) => (
											<tr key={r}>
												{row.map((cell, c) => (
													<td key={c} style={{ textAlign: block.align[c] }}>
														{renderMessageWithLinks(cell, onNavigate)}
													</td>
												))}
											</tr>
										))}
									</tbody>
								</table>
							</div>
						);
					case 'details':
						return (
							<dl className={styles['md-card']} key={key}>
								{block.items.map((item, d) => (
									<div className={styles['md-card__row']} key={d}>
										<dt className={styles['md-card__label']}>{item.label}</dt>
										<dd className={styles['md-card__value']}>{renderMessageWithLinks(item.value, onNavigate)}</dd>
									</div>
								))}
							</dl>
						);
					case 'list': {
						const items = block.items.map((item, l) => <li key={l}>{renderMessageWithLinks(item, onNavigate)}</li>);
						return block.ordered ? (
							<ol className={styles['md-list']} key={key}>{items}</ol>
						) : (
							<ul className={styles['md-list']} key={key}>{items}</ul>
						);
					}
					case 'heading':
						return (
							<div className={styles['md-heading']} key={key}>
								{renderMessageWithLinks(block.text.replace(/\*\*/g, ''), onNavigate)}
							</div>
						);
					case 'source':
						return (
							<div className={styles['md-source']} key={key}>
								<span className={styles['md-source__badge']}>QuickBooks Online data</span>
								{block.rest && <span>{renderMessageWithLinks(block.rest, onNavigate)}</span>}
							</div>
						);
					case 'rule':
						return <hr className={styles['md-rule']} key={key} />;
					default:
						return (
							<div className={styles['md-paragraph']} key={key}>
								{renderMessageWithLinks(block.text, onNavigate)}
							</div>
						);
				}
			})}
		</div>
	);
}

const CURRENCY_FORMAT = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const formatMoney = (value: unknown): string => {
	const num = typeof value === 'number' ? value : Number(value);
	return CURRENCY_FORMAT.format(Number.isFinite(num) ? num : 0);
};

// Renders a successful read-tool lookup as a small stat card/table instead of
// leaving those numbers buried in the reply's prose — one branch per tool
// name, since each returns a differently-shaped result (see READ_TOOLS and
// their handlers in aiAssistantService.ts). Falls back to nothing rendered
// (not an error) for any tool shape this hasn't been taught yet, since the
// prose reply already covers it either way.
function renderDataCard(card: DataCard | undefined, keyPrefix: string) {
	if (!card || !card.data) {
		return null;
	}
	const { tool, data } = card;

	if (tool === 'get_pay_period_totals') {
		const unallocated: string[] = Array.isArray(data.unallocatedEmployees) ? data.unallocatedEmployees : [];
		return (
			<div className={styles['data-card']}>
				<div className={styles['data-card__title']}>{data.payPeriod}</div>
				<div className={styles['data-card__stats']}>
					<div className={styles['data-card__stat']}>
						<span className={styles['data-card__stat-label']}>Total cost</span>
						<span className={styles['data-card__stat-value']}>{formatMoney(data.totalEmployeeCost)}</span>
					</div>
					<div className={styles['data-card__stat']}>
						<span className={styles['data-card__stat-label']}>Allocated</span>
						<span className={styles['data-card__stat-value']}>{formatMoney(data.totalAllocated)}</span>
					</div>
				</div>
				{unallocated.length > 0 && (
					<ul className={styles['data-card__list']}>
						{unallocated.map((name, i) => (
							<li key={`${keyPrefix}-unalloc-${i}`}>{name}</li>
						))}
					</ul>
				)}
			</div>
		);
	}

	if (tool === 'list_pay_periods') {
		const periods: { period: string; journalPublished: boolean }[] =
			Array.isArray(data.periods) ? data.periods : [];
		// The backend sends up to 24 so the model can reason over them, but a
		// chat bubble shouldn't render a 24-row list — the count stat is the
		// actual answer, so show only the most recent few and note the rest.
		const VISIBLE = 6;
		const visible = periods.slice(-VISIBLE).reverse();
		const hiddenCount = Math.max(0, Number(data.totalCount ?? 0) - visible.length);
		return (
			<div className={styles['data-card']}>
				<div className={styles['data-card__title']}>Pay periods</div>
				<div className={styles['data-card__stats']}>
					<div className={styles['data-card__stat']}>
						<span className={styles['data-card__stat-label']}>Total</span>
						<span className={styles['data-card__stat-value']}>{data.totalCount}</span>
					</div>
					<div className={styles['data-card__stat']}>
						<span className={styles['data-card__stat-label']}>Latest</span>
						<span className={styles['data-card__stat-value']}>{data.latestPeriod}</span>
					</div>
				</div>
				{visible.length > 0 && (
					<ul className={styles['data-card__list']}>
						{visible.map((entry, i) => (
							<li key={`${keyPrefix}-period-${i}`}>
								{entry.period}
								{entry.journalPublished ? ' — journal published' : ''}
							</li>
						))}
						{hiddenCount > 0 && <li>+{hiddenCount} earlier</li>}
					</ul>
				)}
			</div>
		);
	}

	// Custom GL mapping rules. Rendered as a card rather than left to the prose
	// because the prose form of this answer is unreadable in a chat column: one
	// rule carries a name, a priority, a status, a multi-clause criteria
	// sentence and a list of account changes, and three rules of that shape run
	// to a screen of unbroken text. The card gives each rule its own block and
	// each override a from → to line.
	if (tool === 'list_gl_mapping_rules') {
		const rules: any[] = Array.isArray(data.rules) ? data.rules : [];
		if (rules.length === 0) {
			return null;
		}
		const activeCount = rules.filter((rule) => String(rule.status || '').startsWith('Active')).length;
		return (
			<div className={styles['data-card']}>
				<div className={styles['data-card__title']}>Custom GL mapping rules — {data.payPeriod}</div>
				<div className={styles['data-card__stats']}>
					<div className={styles['data-card__stat']}>
						<span className={styles['data-card__stat-label']}>Rules</span>
						<span className={styles['data-card__stat-value']}>{rules.length}</span>
					</div>
					<div className={styles['data-card__stat']}>
						<span className={styles['data-card__stat-label']}>Active</span>
						<span className={styles['data-card__stat-value']}>{activeCount}</span>
					</div>
				</div>
				{rules.map((rule, i) => {
					const overrides: any[] = Array.isArray(rule.accountOverrides) ? rule.accountOverrides : [];
					return (
						<div className={styles['data-card__rule']} key={`${keyPrefix}-rule-${i}`}>
							<div className={styles['data-card__rule-head']}>
								<span className={styles['data-card__rule-name']}>
									{rule.priority}. {rule.name}
								</span>
								{/* The backend spells out why an inactive rule matters; only the
								    first word fits here, and it is the part that carries. */}
								<span className={styles['data-card__rule-status']}>
									{String(rule.status || '').startsWith('Active') ? 'Active' : 'Inactive'}
								</span>
							</div>
							{rule.criteria?.matchesWhen && (
								<div className={styles['data-card__rule-meta']}>Matches when {rule.criteria.matchesWhen}</div>
							)}
							{overrides.length === 0 ? (
								<div className={styles['data-card__rule-meta']}>Changes no accounts.</div>
							) : (
								overrides.map((override, j) => (
									<div className={styles['data-card__override']} key={`${keyPrefix}-rule-${i}-ovr-${j}`}>
										<span className={styles['data-card__override-field']}>
											{override.section} · {override.field}
										</span>
										{override.debitAccount && (
											<span className={styles['data-card__override-route']}>
												Debit: {override.defaultDebitAccount} → <strong>{override.debitAccount}</strong>
											</span>
										)}
										{override.creditAccount && (
											<span className={styles['data-card__override-route']}>
												Credit: {override.defaultCreditAccount} → <strong>{override.creditAccount}</strong>
											</span>
										)}
									</div>
								))
							)}
						</div>
					);
				})}
				{data.unresolvedReferences && (
					<div className={styles['data-card__note']}>{data.unresolvedReferences}</div>
				)}
			</div>
		);
	}

	if (tool === 'list_chart_of_accounts') {
		const accounts: any[] = Array.isArray(data.accounts) ? data.accounts : [];
		if (accounts.length === 0) {
			return null;
		}
		return (
			<div className={styles['data-card']}>
				<div className={styles['data-card__title']}>
					Chart of accounts{Number(data.matched) > accounts.length ? ` — ${accounts.length} of ${data.matched}` : ''}
				</div>
				<table className={styles['data-card__table']}>
					<thead>
						<tr>
							<th>Account</th>
							<th>Type</th>
						</tr>
					</thead>
					<tbody>
						{accounts.map((account, i) => (
							<tr key={`${keyPrefix}-acct-${i}`}>
								<td>
									{account.accountNumber ? `${account.accountNumber} · ` : ''}
									{account.fullyQualifiedName || account.name}
								</td>
								<td>{account.accountType}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		);
	}

	if (tool === 'get_expense_spend_by_customer_or_vendor') {
		return (
			<div className={styles['data-card']}>
				<div className={styles['data-card__title']}>{data.name} — {data.period}</div>
				<div className={styles['data-card__stats']}>
					<div className={styles['data-card__stat']}>
						<span className={styles['data-card__stat-label']}>As vendor</span>
						<span className={styles['data-card__stat-value']}>{formatMoney(data.vendorTotal)}</span>
					</div>
					<div className={styles['data-card__stat']}>
						<span className={styles['data-card__stat-label']}>As customer/program</span>
						<span className={styles['data-card__stat-value']}>{formatMoney(data.customerOrProgramTotal)}</span>
					</div>
				</div>
			</div>
		);
	}

	if (tool === 'get_employee_cost_detail') {
		const fields: { field: string; value: unknown }[] = Array.isArray(data.fields) ? data.fields : [];
		return (
			<div className={styles['data-card']}>
				<div className={styles['data-card__title']}>{data.employeeName} — {data.payPeriod}</div>
				{fields.length > 0 && (
					<table className={styles['data-card__table']}>
						<tbody>
							{fields.map((f, i) => (
								<tr key={`${keyPrefix}-field-${i}`}>
									<td>{f.field}</td>
									<td>{formatMoney(f.value)}</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</div>
		);
	}

	if (tool === 'get_expense_transaction_allocation') {
		const lines: { amount: unknown; account: string; customer: string; class: string }[] =
			Array.isArray(data.lines) ? data.lines : [];
		return (
			<div className={styles['data-card']}>
				<div className={styles['data-card__title']}>{data.transaction}</div>
				{lines.length > 0 && (
					<table className={styles['data-card__table']}>
						<thead>
							<tr>
								<th>Account</th>
								<th>Customer</th>
								<th>Class</th>
								<th>Amount</th>
							</tr>
						</thead>
						<tbody>
							{lines.map((l, i) => (
								<tr key={`${keyPrefix}-line-${i}`}>
									<td>{l.account}</td>
									<td>{l.customer}</td>
									<td>{l.class}</td>
									<td>{formatMoney(l.amount)}</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</div>
		);
	}

	if (tool === 'get_time_activity_totals') {
		return (
			<div className={styles['data-card']}>
				<div className={styles['data-card__title']}>{data.employeeName} — {data.period}</div>
				<div className={styles['data-card__stats']}>
					<div className={styles['data-card__stat']}>
						<span className={styles['data-card__stat-label']}>Hours logged</span>
						<span className={styles['data-card__stat-value']}>{data.totalHours}h {data.totalMinutes}m</span>
					</div>
				</div>
			</div>
		);
	}

	if (tool === 'get_time_activity_detail') {
		const entries: { hours: unknown; minute: unknown; customer: string; class: string }[] =
			Array.isArray(data.entries) ? data.entries : [];
		return (
			<div className={styles['data-card']}>
				<div className={styles['data-card__title']}>{data.employeeName} — {data.date}</div>
				{entries.length > 0 && (
					<table className={styles['data-card__table']}>
						<thead>
							<tr>
								<th>Hours</th>
								<th>Customer</th>
								<th>Class</th>
							</tr>
						</thead>
						<tbody>
							{entries.map((e, i) => (
								<tr key={`${keyPrefix}-entry-${i}`}>
									<td>{`${e.hours}h ${e.minute}m`}</td>
									<td>{e.customer}</td>
									<td>{e.class}</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</div>
		);
	}

	return null;
}

// Small sparkle glyph shown on each quick-ask suggestion chip.
const SparkIcon = ({ className }: { className?: string }) => (
	<svg
		width="11"
		height="11"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.9"
		strokeLinecap="round"
		strokeLinejoin="round"
		className={className}
	>
		<path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5 18 18M18 6l-2.5 2.5M8.5 15.5 6 18" />
	</svg>
);

// Marks the notice left behind when a reply is stopped mid-generation.
const StopCircleIcon = () => (
	<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
		<circle cx="12" cy="12" r="9" />
		<rect x="9" y="9" width="6" height="6" rx="1.2" fill="currentColor" stroke="none" />
	</svg>
);

// "Try again" on that same notice.
const RetryIcon = () => (
	<svg
		width="12"
		height="12"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<path d="M20.5 12a8.5 8.5 0 1 1-2.5-6" />
		<path d="M20.5 4v5h-5" />
	</svg>
);

// Shown on the support-escalation card.
const LifebuoyIcon = () => (
	<svg
		width="13"
		height="13"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.9"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<circle cx="12" cy="12" r="9" />
		<circle cx="12" cy="12" r="3.6" />
		<path d="m5.6 5.6 3.9 3.9M14.5 14.5l3.9 3.9M18.4 5.6l-3.9 3.9M9.5 14.5l-3.9 3.9" />
	</svg>
);

// "Take me to a person" said out loud. Matched client-side so the most urgent
// case is also the fastest one — no model round-trip, no waiting on a reply
// that isn't wanted. Everything subtler than this (the same question asked
// three times, a dead end, visible frustration) is left to the model's own
// offer_support_handoff tool, which reads the whole conversation rather than
// one message.
const EXPLICIT_SUPPORT_REQUEST = new RegExp(
	[
		'\\b(?:talk|speak|chat|connect|transfer|escalate)\\b[^.?!]{0,20}\\b(?:human|person|agent|someone|rep|representative|support|staff)\\b',
		'\\b(?:real|actual|live|human)\\s+(?:person|human|agent|support|someone)\\b',
		'\\b(?:contact|reach|email|call)\\s+(?:your\\s+)?(?:support|customer\\s+(?:support|service)|help\\s?desk|a\\s+human)\\b',
		'\\b(?:open|raise|file|create|submit|log)\\b[^.?!]{0,20}\\b(?:ticket|support\\s+case|support\\s+request)\\b',
		'\\bsupport\\s+ticket\\b',
		// "a response from the CAP team", "can the support team look at this".
		// "cap team" and "support team" are unambiguous on their own — nobody
		// asks the assistant about them in any other sense. "your team" needs a
		// response word alongside it, so "invite your team" doesn't match.
		'\\b(?:cap|support|customer\\s+support|help\\s?desk)\\s+team\\b',
		'\\b(?:response|reply|answer|callback|call\\s+back|get\\s+back)\\b[^.?!]{0,30}\\byour\\s+team\\b',
		'\\b(?:someone|somebody|anyone)\\s+from\\s+(?:cap|support|your\\s+team|the\\s+team)\\b',
	].join('|'),
	'i'
);

const isExplicitSupportRequest = (text: string): boolean =>
	EXPLICIT_SUPPORT_REQUEST.test(text);

// The card's opening line. Someone who just asked for a human shouldn't be
// told the conversation has gone on a while — say back the reason they're
// here, then get out of the way.
const supportIntro = (reason?: SupportReason): string => {
	switch (reason) {
		case 'user_asked':
			return 'Of course — here’s a summary of the chat so far to send our support team.';
		case 'repeated_question':
			return 'I keep circling the same answer and it clearly isn’t landing, so here’s a summary for our support team.';
		case 'user_frustrated':
			return 'Sorry this has been a slog. Here’s a summary of the chat for our support team.';
		default:
			return 'This one needs a person — here’s a summary of the chat for our support team.';
	}
};

interface DragPosition {
	top: number;
	left: number;
}

// Starter prompts shown before the first message — curated from the topics
// users most often need help with (per aiAssistantFaq.json on the backend),
// not a fixed generic list. After the first reply, quick-asks switch to the
// assistant's own contextual followUps (see quickAsks below) instead.
const QUICK_ASKS = [
	'How do split rules work?',
	"What's still unallocated?",
	'How do I connect QuickBooks Online?',
	'What is Total Labor Burden?',
];

// How many suggestion chips a stopped turn offers. Matches QUICK_ASKS' length
// so the strip reads the same whether it's the starter set, a reply's own
// follow-ups, or the two topped up with the other.
const STOPPED_FOLLOW_UP_COUNT = 4;

// Live Claude responses take a few seconds — cycle through different words
// instead of a single static "Thinking…" so the wait feels less frozen.
const THINKING_WORDS = [
	'Thinking…',
	'Working on it…',
	'Looking into it…',
	'Considering that…',
	'Putting it together…',
	'Almost there…',
];

// Swaps the label on a steady cadence. Deliberately NOT a typewriter: the
// phrases are different lengths, so typing and deleting them re-measured the
// bubble on every keystroke. In the floating panel that was a few pixels of
// wobble; on the full-width chat page it pumped the whole column in and out.
// One word at a time, crossfaded, with the slot pre-sized to the longest
// phrase (see ThinkingLabel) means the layout never moves at all.
function useCyclingLabel(words: string[], active: boolean, holdMs = 2000) {
	const [index, setIndex] = useState(0);

	useEffect(() => {
		setIndex(0);
		if (!active) {
			return;
		}
		const id = setInterval(() => {
			setIndex((prev) => (prev + 1) % words.length);
		}, holdMs);
		return () => clearInterval(id);
	}, [active, holdMs, words.length]);

	return words[index];
}

// Renders the cycling label without ever changing width: every phrase is laid
// into the same grid cell, so the slot is as wide as the longest one and the
// visible phrase just crossfades inside it. The hidden copies are what hold
// the width — measuring the longest string in JS would need a font-aware
// measurement that breaks the moment the copy or the type scale changes.
const ThinkingLabel = ({ words, current }: { words: string[]; current: string }) => (
	<span className={styles['thinking-label']}>
		{words.map((word) => (
			<span key={word} className={styles['thinking-label__ghost']} aria-hidden="true">
				{word}
			</span>
		))}
		{/* Keyed so React remounts it on change and the fade-in replays. */}
		<span key={current} className={styles['thinking-label__text']}>
			{current}
		</span>
	</span>
);

// Drag-anywhere behaviour shared by the closed pill and the open panel.
// Position is stored as top/left px; until the user drags (or resets), the
// element keeps its default CSS-positioned corner (bottom-right).
function useDraggable() {
	const [position, setPosition] = useState<DragPosition | null>(null);
	const [isDragging, setIsDragging] = useState(false);
	const dragState = useRef<{
		pointerId: number;
		startX: number;
		startY: number;
		originTop: number;
		originLeft: number;
		width: number;
		height: number;
		moved: boolean;
	} | null>(null);
	const wasDraggedRef = useRef(false);

	const onPointerDown = (e: React.PointerEvent<HTMLElement>) => {
		const target = e.target as HTMLElement;
		const el = e.currentTarget;
		// Bail only for a control nested inside the draggable element (e.g. the
		// header's close/maximize buttons) — when the draggable element is
		// itself a button (the collapsed pill), closest() matches el too, and
		// that must not block the drag it's meant to start.
		const closestControl = target.closest('button, input, textarea');
		if (closestControl && closestControl !== el) {
			return;
		}
		const rect = el.getBoundingClientRect();
		dragState.current = {
			pointerId: e.pointerId,
			startX: e.clientX,
			startY: e.clientY,
			originTop: rect.top,
			originLeft: rect.left,
			width: rect.width,
			height: rect.height,
			moved: false,
		};
		el.setPointerCapture(e.pointerId);
		setIsDragging(true);
	};

	const onPointerMove = (e: React.PointerEvent<HTMLElement>) => {
		const state = dragState.current;
		if (!state || state.pointerId !== e.pointerId) {
			return;
		}
		const dx = e.clientX - state.startX;
		const dy = e.clientY - state.startY;
		if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
			state.moved = true;
		}
		const maxLeft = Math.max(window.innerWidth - state.width - 8, 8);
		const maxTop = Math.max(window.innerHeight - state.height - 8, 8);
		setPosition({
			left: Math.min(Math.max(state.originLeft + dx, 8), maxLeft),
			top: Math.min(Math.max(state.originTop + dy, 8), maxTop),
		});
	};

	const onPointerUp = () => {
		if (dragState.current) {
			wasDraggedRef.current = dragState.current.moved;
		}
		dragState.current = null;
		setIsDragging(false);
	};

	const consumeDragClick = () => {
		if (wasDraggedRef.current) {
			wasDraggedRef.current = false;
			return true;
		}
		return false;
	};

	// A position dragged with one element's size (e.g. the small pill) can put a
	// bigger element (the panel) partly off-screen once it swaps in. Nudge it
	// back inside the viewport instead of letting it grow downward/rightward
	// off the edge.
	const keepInView = (el: HTMLElement | null) => {
		if (!el || !position) {
			return;
		}
		const rect = el.getBoundingClientRect();
		const overflowRight = rect.right - (window.innerWidth - 8);
		const overflowBottom = rect.bottom - (window.innerHeight - 8);
		if (overflowRight > 0.5 || overflowBottom > 0.5) {
			setPosition((prev) =>
				prev
					? {
						left: Math.max(prev.left - Math.max(overflowRight, 0), 8),
						top: Math.max(prev.top - Math.max(overflowBottom, 0), 8),
					}
					: prev
			);
		}
	};

	// Drops the pill/panel back to its default bottom-right corner. Also clears
	// any pending drag flag so a stale drag from before closing doesn't eat the
	// next click on the reopened pill (consumeDragClick is only reached via a
	// click, which closing via the X button bypasses).
	const resetPosition = () => {
		setPosition(null);
		wasDraggedRef.current = false;
	};

	const style = position
		? { top: position.top, left: position.left, right: 'auto', bottom: 'auto' }
		: undefined;

	return {
		style,
		isDragging,
		hasCustomPosition: position !== null,
		onPointerDown,
		onPointerMove,
		onPointerUp,
		consumeDragClick,
		keepInView,
		resetPosition,
	};
}

interface ChatBubbleProps {
	// Renders the chat docked into its parent instead of as a floating panel:
	// no launcher pill, no drag, no header chrome, and it fills the space it is
	// given. Used by the /chat page so the conversation lives in the window.
	embedded?: boolean;
}

const ChatBubble = ({ embedded = false }: ChatBubbleProps) => {
	const dispatch = useDispatch<AppDispatch>();
	const { pendingMessage, nonce, openNonce, model, conversationToLoad, loadNonce } = useSelector(
		(state: any) => state.chatBubble
	);
	const firstName = useSelector((state: any) => state?.userProfile?.data?.firstName);
	// The organization selected in the header switcher. Chat is scoped per company,
	// so switching companies starts a fresh thread (see the reset effect below) —
	// continuing a prior company's thread under a new company would mis-file it.
	const activeCompanyId = useSelector(
		(state: any) => state?.companies?.selectedCompanyDetails?.company?.id ?? null
	);

	const navigate = useNavigate();
	const [isOpen, setIsOpen] = useState(false);
	// Kept mounted (with the panel--closing animation class) for the brief
	// window between clicking close and actually unmounting — see closePanel
	// below — so the close transition has something to animate instead of the
	// panel just disappearing instantly.
	const [isClosing, setIsClosing] = useState(false);
	const [isMaximized, setIsMaximized] = useState(false);
	const [messages, setMessages] = useState<Message[]>([]);
	// The persisted thread this conversation belongs to. Null until the first
	// turn is saved (or a saved thread is reopened); sent with every /ai/chat so
	// the backend appends to the right thread, and set from the reply so a
	// brand-new thread's id is remembered for the rest of the session.
	const [conversationId, setConversationId] = useState<string | null>(null);
	// Briefly true right after the header company switches, while the thread
	// resets and the new company's QBO-MCP-backed context takes over — the chat
	// area shows a skeleton during this window.
	const [switchingCompany, setSwitchingCompany] = useState(false);
	// True while a saved thread's transcript (and its attachment URLs) is being
	// fetched after a click — the chat area shows a skeleton until it lands.
	const [loadingConversation, setLoadingConversation] = useState(false);
	const [input, setInput] = useState('');
	const [isSending, setIsSending] = useState(false);
	const [hasUnread, setHasUnread] = useState(false);
	// Separate from hasUnread — this only drives a brief glow animation on the
	// pill right when a reply lands, then clears itself on a timer, whereas
	// hasUnread (the little dot) persists until the user actually opens the
	// panel.
	const [justArrived, setJustArrived] = useState(false);
	const [attachedImage, setAttachedImage] = useState<File | null>(null);
	const [attachedImagePreviewUrl, setAttachedImagePreviewUrl] = useState<string | null>(null);
	// A CSV/XLSX attachment for Import Employee Cost — mutually exclusive with
	// attachedImage (one attachment per message, matching the backend's
	// single-of-each multer fields).
	const [attachedImportFile, setAttachedImportFile] = useState<File | null>(null);
	// Composer skill picker (the "+" menu): the company's enabled skills, the
	// skill pinned for the next message (sent as forcedSkillName), whether the
	// menu is open, and whether the full Skills manager modal is open.
	const [composerSkills, setComposerSkills] = useState<Skill[]>([]);
	const [forcedSkill, setForcedSkill] = useState<Skill | null>(null);
	const [skillMenuOpen, setSkillMenuOpen] = useState(false);
	const [skillsModalOpen, setSkillsModalOpen] = useState(false);
	// True while the composer skill list is being fetched — drives the spinner in
	// the "+" skill button.
	const [skillsLoading, setSkillsLoading] = useState(false);
	// Whether an OS-level file drag is currently over the pill/panel — drives
	// the drop-target overlay. Distinct from any HTML5 drag started inside the
	// page (e.g. selecting text), which dragEnter/dragOver below ignore by
	// checking dataTransfer.types.
	const [isDraggingFile, setIsDraggingFile] = useState(false);
	// Which answer just had its text copied — drives the tick that replaces the
	// copy glyph for a moment. Index rather than boolean so only the message
	// actually copied confirms.
	const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
	// True when the thread is scrolled away from the bottom, so a reply landing
	// off-screen can be jumped to instead of silently missed.
	const [isAwayFromLatest, setIsAwayFromLatest] = useState(false);
	// Index of the question the reader is currently under, for the outline rail.
	const [activeQuestion, setActiveQuestion] = useState<number | null>(null);
	// dragenter/dragleave fire on every child element as the pointer crosses
	// its boundary, not just once for the whole drop zone — a plain boolean
	// flickers off every time the pointer passes over a nested element. This
	// counter only clears isDraggingFile once it's back to zero.
	const dragCounterRef = useRef(0);

	// Smart Import chat flow (see SmartImportChat/) — drives the same reducer/
	// helpers the Employee Cost page's import modal uses, one card per message.
	// At most one message ever carries `smartImportStep` at a time (freeze always
	// runs before the next append), so finding it is a plain scan, not a "last of
	// many" lookup.
	const importChat = useSmartImportChat({
		companyId: localStorage.getItem('companyId') || '',
		appendMessage: (msg) =>
			setMessages((prev) => [
				...prev,
				{ role: 'assistant', text: msg.text, timestamp: Date.now(), smartImportStep: msg.smartImportStep },
			]),
		freezeActiveMessage: (text) =>
			setMessages((prev) => {
				const idx = prev.findIndex((m) => m.smartImportStep);
				if (idx === -1) return prev;
				return prev.map((m, i) => (i === idx ? { ...m, text, smartImportStep: undefined } : m));
			}),
		notifyImported: (payPeriodId) =>
			dispatch(
				notifyDataChanged({
					scope: 'costAllocation',
					companyId: localStorage.getItem('companyId') || '',
					payPeriodId,
				})
			),
	});

	// Employee Direct Allocation chat flow (see DirectAllocationChat/) — same
	// pattern as importChat above, keyed off its own `directAllocationStep`
	// field so freezing one flow's active card never touches the other's.
	const directAllocationChat = useDirectAllocationChat({
		companyId: localStorage.getItem('companyId') || '',
		appendMessage: (msg) =>
			setMessages((prev) => [
				...prev,
				{ role: 'assistant', text: msg.text, timestamp: Date.now(), directAllocationStep: msg.directAllocationStep },
			]),
		freezeActiveMessage: (text) =>
			setMessages((prev) => {
				const idx = prev.findIndex((m) => m.directAllocationStep);
				if (idx === -1) return prev;
				return prev.map((m, i) => (i === idx ? { ...m, text, directAllocationStep: undefined } : m));
			}),
		notifyImported: (payPeriodId) =>
			dispatch(
				notifyDataChanged({
					scope: 'costAllocation',
					companyId: localStorage.getItem('companyId') || '',
					payPeriodId,
				})
			),
	});

	// Custom GL Mapping Rule builder (see MappingRuleChat/) — same pattern as
	// the two flows above, keyed off its own `mappingRuleStep`.
	const mappingRuleChat = useMappingRuleChat({
		appendMessage: (msg) =>
			setMessages((prev) => [
				...prev,
				{ role: 'assistant', text: msg.text, timestamp: Date.now(), mappingRuleStep: msg.mappingRuleStep },
			]),
		// Advances the single builder card rather than appending a new message
		// per step — see the note on Params.setActiveStep.
		setActiveStep: (step) =>
			setMessages((prev) => {
				const idx = prev.findIndex((m) => m.mappingRuleStep);
				if (idx === -1) return prev;
				return prev.map((m, i) => (i === idx ? { ...m, mappingRuleStep: step } : m));
			}),
		// The rule itself is created in Settings → Configurations' own Add Rule
		// modal, so the chat's last act is to open the drawer there and get out
		// of the way. Closing the panel matters: the drawer covers it, and
		// leaving it open behind the modal makes the chat look stuck.
		goToConfigurations: (path) => {
			closePanel();
			navigate(path);
		},
	});

	// Settings → Time Rules handoff (see TimeRuleChat/) — same shape as the
	// mapping-rule one above, keyed off its own `timeRuleStep`.
	const timeRuleChat = useTimeRuleChat({
		appendMessage: (msg) =>
			setMessages((prev) => [
				...prev,
				{ role: 'assistant', text: msg.text, timestamp: Date.now(), timeRuleStep: msg.timeRuleStep },
			]),
		setActiveStep: (step) =>
			setMessages((prev) => {
				const idx = prev.findIndex((m) => m.timeRuleStep);
				if (idx === -1) return prev;
				return prev.map((m, i) => (i === idx ? { ...m, timeRuleStep: step } : m));
			}),
		goToTimeRules: (path) => {
			closePanel();
			navigate(path);
		},
	});

	// Holds the attached file between "what would you like to do with this
	// file?" being asked and the user picking an option — see the importFile
	// branch in sendMessage and the importChoicePending card below.
	const pendingImportChoiceFileRef = useRef<File | null>(null);

	const threadRef = useRef<HTMLDivElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const composerRef = useRef<HTMLInputElement>(null);
	// Held for the duration of one in-flight /ai/chat request so the user can
	// abort it. A long Tier 3 turn can run for many seconds; without this the
	// only options were to wait it out or close the panel.
	const abortRef = useRef<AbortController | null>(null);
	// Set true right before a company-switch abort so sendMessage's catch skips
	// its "stopped" placeholder — the thread has already been cleared for the new
	// company, and a stopped bubble there would be noise.
	const suppressStopNoticeRef = useRef(false);
	// A monotonically increasing token. A send captures the current value; any
	// navigation away (new chat, open a saved thread, company switch) bumps it.
	// When a reply comes back, we compare: if the token moved, the user has left
	// that chat, so the request still FINISHES and SAVES (its WebSocket "reply
	// ready" dot flags it) but its result is NOT written into whatever chat is now
	// open. This replaces aborting the request — which lost the message entirely.
	const sendSeqRef = useRef(0);
	// Set when the user waves the card away. It suppresses only the AUTOMATIC
	// offers — re-offering unprompted would nag exactly the person we've already
	// failed. Asking for a human outright, or using Contact support in the
	// header, still works every time.
	const supportDismissedRef = useRef(false);
	// True while a card is on screen awaiting a decision, so a second trigger
	// (the model offering while the user is already mid-edit, say) doesn't stack
	// another one underneath it.
	const supportCardOpenRef = useRef(false);
	const objectUrlsRef = useRef<string[]>([]);
	const isOpenRef = useRef(isOpen);
	const drag = useDraggable();
	const thinkingText = useCyclingLabel(THINKING_WORDS, isSending);

	// sendMessage's async reply can land after the user has closed the panel;
	// read the live open-state from a ref rather than the stale closure value.
	useEffect(() => {
		isOpenRef.current = isOpen;
	}, [isOpen]);

	const openChat = () => {
		setIsOpen(true);
		setHasUnread(false);
	};

	// Mirrors openChat's instant show with an animated hide: keep the panel
	// mounted (isClosing) long enough for panel-out to play, then actually
	// swap back to the pill. The duration here must match panel--closing's
	// own CSS animation-duration in index.module.scss.
	const closePanel = () => {
		setIsClosing(true);
		setTimeout(() => {
			setIsOpen(false);
			setIsClosing(false);
			setIsMaximized(false);
			drag.resetPosition();
		}, 160);
	};

	useEffect(() => {
		threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight });
	}, [messages, isSending]);

	// The panel can inherit a drag position sized for the small pill; once it
	// swaps in at its full size, pull it back on-screen if it now overflows.
	// The thread div itself only exists in the DOM while open, so reopening
	// with existing history needs its own scroll-to-bottom — the [messages,
	// isSending] effect above doesn't fire again just because isOpen changed.
	useLayoutEffect(() => {
		if (isOpen) {
			drag.keepInView(panelRef.current);
			threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen]);

	// Toggling maximize swaps the panel's footprint (386×435 <-> 640×680) in
	// place — a custom drag position that fit the smaller size can push the
	// larger one off-screen, so re-clamp it after the size change lands.
	useLayoutEffect(() => {
		drag.keepInView(panelRef.current);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isMaximized]);

	useEffect(() => {
		return () => {
			objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
		};
	}, []);

	const handleAttachClick = () => {
		fileInputRef.current?.click();
	};

	// Loads the company's ENABLED skills for the composer picker (disabled ones
	// aren't used by the assistant, so they aren't offered here). Called when the
	// menu opens and after the Skills manager closes, so edits show immediately.
	const loadComposerSkills = () => {
		const companyId = localStorage.getItem('companyId');
		if (!companyId) return;
		setSkillsLoading(true);
		getApi('/ai/skills', { companyId })
			.then((res) => {
				const list = res?.data?.data?.skills;
				if (Array.isArray(list)) {
					const enabled = (list as Skill[]).filter((s) => s.enabled);
					setComposerSkills(enabled);
					// Drop a pinned skill that was disabled or deleted while pinned.
					setForcedSkill((prev) => (prev && !enabled.some((s) => s.id === prev.id) ? null : prev));
				}
			})
			.catch(() => {
				/* a failed skills fetch just leaves the picker empty */
			})
			.finally(() => setSkillsLoading(false));
	};

	const toggleSkillMenu = () => {
		const next = !skillMenuOpen;
		setSkillMenuOpen(next);
		if (next) loadComposerSkills();
	};

	const pickSkill = (skill: Skill) => {
		setForcedSkill(skill);
		setSkillMenuOpen(false);
	};

	const openSkillsManager = () => {
		setSkillMenuOpen(false);
		setSkillsModalOpen(true);
	};

	// Shared by the file-picker input and drag-and-drop — both end up with a
	// single File and need the same type/size routing and rejection messages.
	const processFile = (file: File) => {
		// Route by extension first — a payroll export selected via "All Files"
		// still has a real extension even when its mimetype is unreliable
		// (see the constant comment above), whereas an image always reports a
		// real image/* mimetype the browser can be trusted for.
		if (ALLOWED_IMPORT_FILE_EXTENSIONS.includes(fileExtension(file.name))) {
			if (file.size > MAX_IMPORT_FILE_SIZE_BYTES) {
				toastText(`That file is larger than ${MAX_IMPORT_FILE_SIZE_BYTES / (1024 * 1024)}MB.`, 'error');
				return;
			}
			removeAttachedImage();
			setAttachedImportFile(file);
			return;
		}

		if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
			toastText('Attach a PNG/JPEG/WEBP screenshot, or a CSV/XLSX payroll file.', 'error');
			return;
		}
		if (file.size > MAX_IMAGE_SIZE_BYTES) {
			toastText('Image must be 5MB or smaller.', 'error');
			return;
		}
		const previewUrl = URL.createObjectURL(file);
		objectUrlsRef.current.push(previewUrl);
		removeAttachedImportFile();
		setAttachedImage(file);
		setAttachedImagePreviewUrl(previewUrl);
	};

	const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		e.target.value = '';
		if (!file) {
			return;
		}
		processFile(file);
	};

	// True only for a real OS file drag (dataTransfer.types includes "Files"),
	// not an in-page text/element drag — those also fire these same events.
	const isFileDrag = (e: React.DragEvent) => Array.from(e.dataTransfer?.types || []).includes('Files');

	const handleDragEnter = (e: React.DragEvent) => {
		if (!isFileDrag(e)) {
			return;
		}
		e.preventDefault();
		dragCounterRef.current += 1;
		setIsDraggingFile(true);
	};

	const handleDragOver = (e: React.DragEvent) => {
		if (!isFileDrag(e)) {
			return;
		}
		// Required so the element becomes a valid drop target — without this
		// the drop event above never fires and the browser just opens the file.
		e.preventDefault();
		e.dataTransfer.dropEffect = 'copy';
	};

	const handleDragLeave = (e: React.DragEvent) => {
		if (!isFileDrag(e)) {
			return;
		}
		e.preventDefault();
		dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
		if (dragCounterRef.current === 0) {
			setIsDraggingFile(false);
		}
	};

	const handleFileDrop = (e: React.DragEvent) => {
		if (!isFileDrag(e)) {
			return;
		}
		e.preventDefault();
		dragCounterRef.current = 0;
		setIsDraggingFile(false);
		if (isSending) {
			return;
		}
		const file = e.dataTransfer.files?.[0];
		if (!file) {
			return;
		}
		if (!isOpen) {
			openChat();
		}
		processFile(file);
	};

	const removeAttachedImage = () => {
		setAttachedImage(null);
		setAttachedImagePreviewUrl(null);
	};

	const removeAttachedImportFile = () => {
		setAttachedImportFile(null);
	};

	// Flags a reply that arrived while the panel was closed: hasUnread drives
	// the persistent dot (cleared only when the user actually opens the
	// panel), justArrived drives a brief glow on the pill that clears itself
	// on a timer regardless of whether the user ever opens it.
	const notifyArrival = () => {
		if (isOpenRef.current) {
			return;
		}
		setHasUnread(true);
		setJustArrived(true);
		setTimeout(() => setJustArrived(false), 2000);
	};

	// `replaceFromIndex` re-asks from a point in the thread instead of appending
	// to the end: everything from that index down is dropped first. Used by the
	// stopped-turn retry, so re-sending the question that was cut short reads as
	// one turn rather than leaving a duplicate of it above.
	const sendMessage = async (text: string, replaceFromIndex?: number) => {
		const trimmed = text.trim();
		const image = attachedImage;
		const imagePreviewUrl = attachedImagePreviewUrl;
		const importFile = attachedImportFile;
		// A bare attachment with no question is still a valid ask — give it a
		// sensible default caption instead of blocking on empty text.
		const effectiveText =
			trimmed ||
			(image ? 'What do you see in this screenshot?' : importFile ? 'Import this file.' : '');
		if (!effectiveText || isSending) {
			return;
		}

		// The thread this turn builds on: a retry rewinds past the question being
		// re-asked, and stopped-turn notices are dropped — they're a UI artifact,
		// not something the assistant actually said.
		const base = replaceFromIndex !== undefined ? messages.slice(0, replaceFromIndex) : messages;
		const history = base.filter((m) => !m.variant);

		// A still-open import card (not yet confirmed/cancelled) from earlier in
		// this thread — only relevant when this message attaches no NEW file
		// (a fresh attachment always starts its own proposal from scratch).
		// Sent along as `pendingImportContext` so a follow-up like "view all
		// employee records" or "apply $500 to everyone's salary" can be
		// resolved against it server-side (see aiAssistantService.ts).
		let pendingImportIndex = -1;
		if (!importFile) {
			for (let i = base.length - 1; i >= 0; i--) {
				const candidate = base[i];
				if (candidate.role === 'assistant' && candidate.pendingAction?.type === 'import_employee_cost' && candidate.actionStatus === 'pending') {
					pendingImportIndex = i;
					break;
				}
			}
		}
		const pendingImportContext = pendingImportIndex !== -1 ? base[pendingImportIndex].pendingAction : undefined;
		setMessages((prev) => [
			...(replaceFromIndex !== undefined ? prev.slice(0, replaceFromIndex) : prev),
			{
				role: 'user',
				text: effectiveText,
				timestamp: Date.now(),
				imageUrl: imagePreviewUrl || undefined,
				importFileName: importFile?.name,
				skillName: forcedSkill?.name,
			},
		]);
		setInput('');
		removeAttachedImage();
		removeAttachedImportFile();

		// Someone asking for a human outright gets one — no answer attempt first,
		// no model round-trip to sit through. Skipped when something is attached,
		// since "can someone look at this?" with a screenshot is usually a
		// question for the assistant, and the model can still hand off itself.
		// Not while a card is already open awaiting a decision — intercepting then
		// would swallow the message with nothing to show for it.
		if (
			!image &&
			!importFile &&
			!supportCardOpenRef.current &&
			isExplicitSupportRequest(effectiveText)
		) {
			offerSupportEscalation(
				[...history, { role: 'user' as const, text: effectiveText, timestamp: Date.now() }],
				'user_asked'
			);
			return;
		}

		// A CSV/XLSX attachment is driven entirely client-side — it never reaches
		// /ai/chat, so the AI service's own import_employee_cost path is only
		// relevant to messages that attach no new file (see pendingImportIndex
		// above). Rather than assuming Smart Import, ask which import this file
		// is for; the choice card (importChoicePending, rendered below) opens
		// the matching import modal (see chooseImportFlow) once the user picks.
		if (importFile) {
			pendingImportChoiceFileRef.current = importFile;
			setMessages((prev) => [
				...prev,
				{
					role: 'assistant',
					text: 'What would you like to do with this file?',
					timestamp: Date.now(),
					importChoicePending: true,
				},
			]);
			return;
		}

		setIsSending(true);
		// This send's token. If the user navigates away before the reply, the token
		// moves and we stop writing this reply into the (now different) open chat.
		const mySeq = sendSeqRef.current;
		// Whether this send started a brand-new chat (no id yet), and the id its
		// running indicator should be cleared under when it finishes — filled in
		// once the reply returns the (possibly newly-created) conversation id.
		const sendWasNewChat = !conversationId;
		let finalConversationId: string | null = conversationId ?? null;

		// Mark this chat as "working" in the sidebar for the duration of the turn —
		// keyed per-conversation, so it keeps its spinner even if the user navigates
		// away, and is cleared only when THIS reply finishes. An existing thread
		// gets a spinner on its row; a brand-new chat shows a temporary row.
		dispatch(
			startRunningConversation({
				conversationId: conversationId ?? null,
				title: effectiveText.trim().slice(0, 80) || 'New chat',
			})
		);

		const controller = new AbortController();
		abortRef.current = controller;

		try {
			let response: any;
			const companyId = localStorage.getItem('companyId') || undefined;

			// One multipart body serves both the streaming attempt and the
			// non-streaming fallback — /ai/chat/stream shares /ai/chat's multer
			// middleware, so it takes the same form (with or without an image).
			const buildFormData = () => {
				const fd = new FormData();
				fd.append('message', effectiveText);
				fd.append('history', JSON.stringify(history));
				if (image) fd.append('image', image);
				if (companyId) fd.append('companyId', companyId);
				if (conversationId) fd.append('conversationId', conversationId);
				if (pendingImportContext) fd.append('pendingImportContext', JSON.stringify(pendingImportContext));
				fd.append('model', model);
				if (forcedSkill) fd.append('forcedSkillName', forcedSkill.name);
				return fd;
			};

			// Try the streaming endpoint first so the reply reveals itself as it is
			// generated. A unique key identifies the in-progress bubble so each
			// delta updates exactly it (index lookups race across async setState).
			// If streaming fails before any content, fall back to the plain
			// /ai/chat request below — the chat never depends on streaming working.
			const streamKey = `stream-${mySeq}-${Date.now()}`;
			let streamedAny = false;
			let streamText = '';
			try {
				await postApiStream(
					'/ai/chat/stream',
					buildFormData(),
					(evt: any) => {
						if (evt?.type === 'delta' && typeof evt.text === 'string') {
							streamText += evt.text;
							if (sendSeqRef.current !== mySeq) return; // user moved on
							if (!streamedAny) {
								streamedAny = true;
								setMessages((prev) => [
									...prev,
									{ role: 'assistant', text: streamText, timestamp: Date.now(), streaming: true, streamKey },
								]);
							} else {
								setMessages((prev) => prev.map((m) => (m.streamKey === streamKey ? { ...m, text: streamText } : m)));
							}
						} else if (evt?.type === 'done') {
							// The authoritative result (source-labelled reply + signed
							// cards). Shaped like an axios response so everything below is
							// unchanged.
							response = { data: { data: evt.data } };
						} else if (evt?.type === 'error') {
							// A server-side failure mid-stream: surface it (don't silently
							// re-run on /ai/chat, which would duplicate the turn).
							const e: any = new Error(evt.message || 'The assistant hit an error.');
							e.response = { status: evt.status ?? 500, data: { message: evt.message } };
							e.noFallback = true;
							throw e;
						}
					},
					controller.signal
				);
			} catch (streamErr: any) {
				if (controller.signal.aborted) throw streamErr; // handled as a Stop below
				const status = streamErr?.response?.status ?? streamErr?.status;
				// Don't fall back when the failure is terminal (a server 'error'
				// event, a rate limit, or a drop AFTER partial text already showed) —
				// re-running /ai/chat would double the work/persistence. Otherwise
				// (no stream support, 401, network, early 5xx) fall back silently.
				const terminal = streamErr?.noFallback || status === 429 || streamedAny;
				if (streamedAny) {
					setMessages((prev) => prev.filter((m) => m.streamKey !== streamKey));
					streamedAny = false;
				}
				if (terminal) throw streamErr;
				response = undefined;
			}

			if (!response) {
				// Fallback: the original non-streaming request, unchanged.
				if (image) {
					response = await postApiNoHeader('/ai/chat', buildFormData(), controller.signal);
				} else {
					response = await postApi(
						'/ai/chat',
						{
							message: effectiveText,
							history,
							companyId,
							pendingImportContext,
							model,
							conversationId: conversationId ?? undefined,
							forcedSkillName: forcedSkill?.name,
						},
						undefined,
						undefined,
						controller.signal
					);
				}
			}
			const reply = response?.data?.data?.reply || "I couldn't generate a response.";
			const pendingAction: PendingAction | undefined = response?.data?.data?.pendingAction;
			const followUps: string[] | undefined = response?.data?.data?.followUps;
			const pendingNavigation: PendingNavigation | undefined = response?.data?.data?.pendingNavigation;
			const dataCard: DataCard | undefined = response?.data?.data?.dataCard;
			const fileDownload: FileDownload | undefined = response?.data?.data?.fileDownload;
			const supportHandoff: { reason: SupportReason } | undefined =
				response?.data?.data?.supportHandoff;
			const mappingRuleBuilder: { mode: 'create' | 'edit'; ruleId?: string } | undefined =
				response?.data?.data?.mappingRuleBuilder;
			const timeRuleBuilder:
				| { mode: 'create' | 'edit'; ruleId?: string; ruleType?: TimeRuleType }
				| undefined = response?.data?.data?.timeRuleBuilder;
			const isCurrentSend = sendSeqRef.current === mySeq;
			if (isCurrentSend) {
			setMessages((prev) => {
				// A follow-up ("apply $500 to everyone") that produced a fresh import
				// card replaces the old one — mark it superseded so only the new
				// card's Confirm button can actually write anything. A follow-up that
				// just READ the old card (e.g. "view all employee records", which
				// carries no pendingAction of its own) leaves it untouched.
				const withSupersede =
					pendingImportIndex !== -1 && pendingAction?.type === 'import_employee_cost'
						? prev.map((m, i) => (i === pendingImportIndex ? { ...m, actionStatus: 'superseded' as const } : m))
						: prev;
				const finalized = {
					role: 'assistant' as const,
					text: reply,
					timestamp: Date.now(),
					pendingAction,
					actionStatus: (pendingAction ? 'pending' : undefined) as ActionStatus | undefined,
					pendingNavigation,
					navigationStatus: (pendingNavigation ? 'pending' : undefined) as NavigationStatus | undefined,
					followUps,
					dataCard,
					fileDownload,
					downloadStatus: (fileDownload ? 'ready' : undefined) as DownloadStatus | undefined,
				};
				// If the reply streamed into a live bubble, finalize THAT bubble
				// (swap its streamed text for the authoritative reply and attach the
				// cards) rather than appending a second one.
				if (streamedAny) {
					return withSupersede.map((m) =>
						m.streamKey === streamKey ? { ...m, ...finalized, streaming: false, streamKey: undefined } : m
					);
				}
				return [...withSupersede, finalized];
			});
			notifyArrival();
			}

			// Remember which persisted thread this turn landed in (a brand-new
			// thread's id arrives here on the first turn), and nudge the sidebar's
			// thread list to refetch so the new/updated conversation shows up.
			const returnedConversationId: string | undefined =
				response?.data?.data?.conversationId;
			// The id this send's running indicator is cleared under in `finally`.
			if (returnedConversationId) {
				finalConversationId = returnedConversationId;
			}
			const startedNewThread =
				!!returnedConversationId && returnedConversationId !== conversationId;
			if (isCurrentSend && startedNewThread) {
				setConversationId(returnedConversationId as string);
				// Highlight the just-created thread as the selected one in the sidebar.
				dispatch(setActiveConversation(returnedConversationId as string));
			}
			if (returnedConversationId) {
				// One refetch per reply so the sidebar shows the new/updated thread.
				dispatch(bumpConversations());
				// This reply was shown live, so this thread is not unread — clear any
				// dot the WebSocket "reply ready" notice may have set for it in the
				// race with this HTTP response.
				if (isCurrentSend) dispatch(clearConversationUnread(returnedConversationId));
				// (A brand-new thread's backend-generated title is picked up on the
				// next refetch rather than by a second, timed poll here — one fewer
				// /ai/conversations call per new thread.)
			}

			// The backend recognised "create a mapping rule" and wants the picker
			// card rather than a sentence-parsing attempt. Opened after the reply
			// bubble lands so the explanation reads above the form, and awaited
			// nowhere — open() appends its own card message when its lists arrive.
			if (isCurrentSend && mappingRuleBuilder) {
				mappingRuleChat.open(mappingRuleBuilder.mode, mappingRuleBuilder.ruleId);
			}

			if (isCurrentSend && timeRuleBuilder) {
				timeRuleChat.open(timeRuleBuilder.mode, timeRuleBuilder.ruleId, timeRuleBuilder.ruleType);
			}

			// The model called offer_support_handoff — it has decided that answering
			// again isn't going to get there. Its reply lands as a normal bubble;
			// the card goes underneath it.
			if (isCurrentSend && supportHandoff && !supportDismissedRef.current) {
				offerSupportEscalation(
					[
						...history,
						{ role: 'user' as const, text: effectiveText, timestamp: Date.now() },
						{ role: 'assistant' as const, text: reply, timestamp: Date.now() },
					],
					supportHandoff.reason
				);
			}
		} catch (err) {
			// A user-initiated stop is not a failure — no toast, no apology, and
			// no notifyArrival (nothing arrived). Just mark the turn as stopped so
			// the thread doesn't look like the question was silently dropped.
			if (controller.signal.aborted) {
				// A switch-triggered abort (company change) has already cleared the
				// thread — don't drop a "stopped" artifact into the fresh chat.
				if (suppressStopNoticeRef.current) {
					suppressStopNoticeRef.current = false;
					return;
				}
				setMessages((prev) => {
					// A stopped turn has no follow-ups of its own, but the chips
					// shouldn't disappear just because a reply was cut short — carry
					// the last reply's suggestions forward so there's still an obvious
					// next move. A reply usually offers two or three, which leaves the
					// strip looking thinner than every other turn's, so the starter
					// asks top it up to a full four (deduped, carried ones first).
					const carried =
						[...prev]
							.reverse()
							.find((m) => m.role === 'assistant' && m.followUps?.length)?.followUps ?? [];
					return [
						...prev,
						{
							role: 'assistant',
							text: '',
							timestamp: Date.now(),
							variant: 'stopped' as const,
							followUps: [
								...carried,
								...QUICK_ASKS.filter((q) => !carried.includes(q)),
							].slice(0, STOPPED_FOLLOW_UP_COUNT),
						},
					];
				});
				return;
			}
			if (sendSeqRef.current === mySeq) {
			toastText('Something went wrong reaching the AI assistant.', 'error');
			// A 4xx carries a message written for the user (rate limit, file too
			// large, …); anything else is a connection/server failure. Either way
			// the turn ends with a one-tap resend, never a dead end.
			const status = (err as any)?.response?.status;
			const serverMessage = (err as any)?.response?.data?.message;
			const text =
				status >= 400 && status < 500 && typeof serverMessage === 'string' && serverMessage.trim()
					? serverMessage.trim()
					: "I couldn't reach the assistant just now, so that message didn't get an answer. Tap below to send it again.";
			setMessages((prev) => [
				...prev,
				{
					role: 'assistant',
					text,
					timestamp: Date.now(),
					followUps: effectiveText.trim() ? [effectiveText.trim().slice(0, 200)] : undefined,
				},
			]);
			notifyArrival();
			}
		} finally {
			// Clear THIS send's running indicator no matter which chat is now open —
			// it's keyed per-conversation, so finishing a backgrounded reply removes
			// exactly its own spinner and nothing else.
			dispatch(endRunningConversation(finalConversationId));
			if (sendWasNewChat) {
				dispatch(clearPendingNewChat());
			}
			// Only the still-current send resets the composer — a backgrounded reply
			// finishing must not disturb a newer one.
			if (sendSeqRef.current === mySeq) {
				abortRef.current = null;
				setIsSending(false);
			}
		}
	};

	// Reopens a saved thread selected from the sidebar. Rebuilds the message list
	// from the persisted transcript (text + the display extras the backend keeps:
	// follow-ups, data cards, navigation) and resolves a fresh, short-lived
	// presigned URL for each image attachment so its preview renders again;
	// non-image attachments show as their filename, downloaded on click.
	const loadConversation = async (id: string) => {
		// Don't abort the in-flight reply from the chat we're leaving — let it
		// finish and save (its row keeps its spinner and it gets an unread dot).
		// Bumping the token just keeps its result from landing in this view.
		sendSeqRef.current += 1;
		setIsSending(false);
		openChat();
		// Clear the current thread and show a skeleton while the transcript loads.
		setMessages([]);
		setLoadingConversation(true);
		try {
			const res = await getApi(`/ai/conversations/${id}`);
			const conv = res?.data?.data;
			if (!conv || !Array.isArray(conv.messages)) {
				return;
			}
			const restored: Message[] = await Promise.all(
				conv.messages.map(async (m: any): Promise<Message> => {
					const meta = m?.metadata || {};
					const msg: Message = {
						role: m.role === 'user' ? 'user' : 'assistant',
						text: typeof m.text === 'string' ? m.text : '',
						timestamp: m.createdAt ? new Date(m.createdAt).getTime() : Date.now(),
					};
					if (m.role === 'user') {
						// The skill the user pinned for this message, so a reopened thread
						// shows the same "✦ skill-name" tag it did when the message was sent.
						if (typeof meta.skillName === 'string' && meta.skillName) msg.skillName = meta.skillName;
					}
					if (m.role === 'assistant') {
						if (Array.isArray(meta.followUps)) msg.followUps = meta.followUps;
						if (meta.pendingNavigation) {
							msg.pendingNavigation = meta.pendingNavigation;
							// Restore the card as actionable — without a status the card
							// renders as an empty box (its button only shows when 'pending').
							// Navigation is non-destructive, so it's safe to re-offer.
							msg.navigationStatus = 'pending';
						}
						if (meta.dataCard) msg.dataCard = meta.dataCard;
						// Re-fetched under the current session on click, so it stays usable.
						if (meta.fileDownload) {
							msg.fileDownload = meta.fileDownload;
							msg.downloadStatus = 'ready';
						}
					}
					const attachments = Array.isArray(m.attachments) ? m.attachments : [];
					const nonImage = attachments.find((a: any) => a.kind !== 'image');
					if (nonImage) msg.importFileName = nonImage.fileName;
					const imageAtt = attachments.find((a: any) => a.kind === 'image');
					if (imageAtt) {
						try {
							const urlRes = await getApi(`/ai/chat/attachments/${imageAtt.id}/url`);
							const url = urlRes?.data?.data?.url;
							if (url) msg.imageUrl = url;
						} catch {
							// A missing/expired object just renders without its preview.
						}
					}
					return msg;
				})
			);
			setMessages(restored);
			setConversationId(conv.id);
		} catch {
			toastText('Could not open that conversation.', 'error');
		} finally {
			setLoadingConversation(false);
		}
	};

	// ---------------------------------------------------------------------
	// Support escalation
	//
	// Two ways in, deliberately neither of them a message count — a long session
	// means the assistant is being used, not that it's failing:
	//   1. The user asking for a human outright (isExplicitSupportRequest).
	//   2. The model calling offer_support_handoff when it reads the room —
	//      repeated question, dead end, or visible frustration.
	// Both land here: the assistant drafts an overview of the conversation,
	// the user edits it if they want, and sending it files an Outseta support
	// ticket with the transcript attached.
	// ---------------------------------------------------------------------

	// Resolves the "what would you like to do with this file?" chooser: freeze
	// that card into inert history, hand the file off to the target page (see
	// utils/pendingChatImport), close the chat panel, and navigate there so
	// the user finishes the import in the real modal instead of in-chat.
	const chooseImportFlow = (kind: 'smart' | 'directAllocation') => {
		const file = pendingImportChoiceFileRef.current;
		if (!file) return;
		pendingImportChoiceFileRef.current = null;
		const label = kind === 'smart' ? 'Import Employee Cost' : 'Import Direct Employee Cost';
		setMessages((prev) => {
			const idx = prev.findIndex((m) => m.importChoicePending);
			if (idx === -1) return prev;
			return prev.map((m, i) => (i === idx ? { ...m, text: `Opening ${label}...`, importChoicePending: false } : m));
		});
		requestChatImport(kind, file);
		setAttachedImportFile(null);
		closePanel();
		navigate(kind === 'smart' ? '/employee-costs' : '/time-activity');
	};

	// The escalation card lives in the thread like any other message, but only
	// one exists at a time, so it's addressed by "the last support card" rather
	// than by a captured index — which a retry or an edit-and-resend could
	// otherwise invalidate mid-edit.
	const updateSupportCard = (patch: Partial<Message>) => {
		setMessages((prev) => {
			let cardIndex = -1;
			for (let i = prev.length - 1; i >= 0; i--) {
				if (prev[i].variant === 'support') {
					cardIndex = i;
					break;
				}
			}
			if (cardIndex === -1) {
				return prev;
			}
			return prev.map((m, i) => (i === cardIndex ? { ...m, ...patch } : m));
		});
	};

	// Card-only turns carry no bubble text (the mapping-rule builder's card IS
	// the message), and an empty text block is rejected by the model API — so
	// they are dropped here as well as server-side rather than sent as blanks.
	const toApiHistory = (thread: Message[]) =>
		thread
			.filter((m) => !m.variant && m.text.trim().length > 0)
			.map((m) => ({ role: m.role, text: m.text }));

	const offerSupportEscalation = async (thread: Message[], reason: SupportReason) => {
		if (supportCardOpenRef.current) {
			return;
		}
		supportCardOpenRef.current = true;

		setMessages((prev) => [
			...prev,
			{
				role: 'assistant',
				text: '',
				timestamp: Date.now(),
				variant: 'support' as const,
				supportReason: reason,
				supportStatus: 'drafting' as const,
			},
		]);
		notifyArrival();

		try {
			const response = await postApi('/ai/support/summary', {
				history: toApiHistory(thread),
			});
			updateSupportCard({
				supportStatus: 'ready',
				supportSubject: response?.data?.data?.subject || '',
				supportSummary: response?.data?.data?.summary || '',
			});
		} catch {
			// The draft is a convenience, not the point — if it fails, still let the
			// user reach support by writing the overview themselves.
			updateSupportCard({
				supportStatus: 'ready',
				supportSubject: '',
				supportSummary: '',
			});
		}
	};

	const sendSupportTicket = async (card: Message) => {
		const summary = (card.supportSummary ?? '').trim();
		if (!summary) {
			toastText('Please describe the issue before sending it to support.', 'error');
			return;
		}

		updateSupportCard({ supportStatus: 'sending' });
		try {
			const response = await postApi('/ai/support/ticket', {
				subject: card.supportSubject,
				summary,
				history: toApiHistory(messages),
			});
			supportCardOpenRef.current = false;
			updateSupportCard({
				supportStatus: 'sent',
				supportTicketRef: response?.data?.data?.ticketId ?? null,
			});
		} catch (err: any) {
			toastText(
				err?.response?.data?.message ||
					"We couldn't send that to support just now. Please try again.",
				'error'
			);
			// Back to 'ready' rather than a dead-end error state — the edited text is
			// still in the card, so retrying is one click.
			updateSupportCard({ supportStatus: 'ready' });
		}
	};

	const dismissSupportCard = () => {
		supportCardOpenRef.current = false;
		supportDismissedRef.current = true;
		updateSupportCard({ supportStatus: 'dismissed' });
	};

	// Aborts the in-flight request. The abort propagates: the backend listens for
	// the dropped connection and cancels its Anthropic call, so generation
	// actually stops rather than finishing an answer nobody will read.
	const stopSending = () => {
		abortRef.current?.abort();
	};

	// The question a stopped turn would re-ask, or -1 when there's nothing to
	// retry — a question that carried a screenshot or a spreadsheet can't be
	// replayed, since the browser File object is gone once it has been sent.
	const retryableQuestionIndex = (msgIndex: number) => {
		for (let i = msgIndex - 1; i >= 0; i--) {
			const candidate = messages[i];
			if (candidate.role !== 'user') {
				continue;
			}
			return candidate.imageUrl || candidate.importFileName ? -1 : i;
		}
		return -1;
	};

	// Re-asks the stopped question. Both the notice and the original question
	// are dropped first (see sendMessage's replaceFromIndex) so the retry
	// replaces the abandoned turn instead of stacking a near-identical one
	// underneath it.
	const retryStopped = (msgIndex: number) => {
		const userIndex = retryableQuestionIndex(msgIndex);
		if (isSending || userIndex === -1) {
			return;
		}
		sendMessage(messages[userIndex].text, userIndex);
	};

	// Puts an earlier question back in the composer and drops it (and
	// everything after it) from the thread, so resending re-asks from that point
	// instead of appending a near-duplicate turn below the original.
	const startEditMessage = (msgIndex: number) => {
		const target = messages[msgIndex];
		if (!target || target.role !== 'user' || isSending) {
			return;
		}
		setInput(target.text);
		setMessages((prev) => prev.slice(0, msgIndex));
		composerRef.current?.focus();
	};

	// Marks the pendingAction on the message at msgIndex with a new status —
	// used to disable the card's buttons immediately and reflect the outcome,
	// without touching any other message in the thread.
	const setActionStatus = (msgIndex: number, status: ActionStatus) => {
		setMessages((prev) =>
			prev.map((m, i) => (i === msgIndex ? { ...m, actionStatus: status } : m))
		);
	};

	const cancelAction = (msgIndex: number) => {
		setActionStatus(msgIndex, 'cancelled');
		setMessages((prev) => [
			...prev,
			{ role: 'assistant', text: "Okay, I won't make that change.", timestamp: Date.now() },
		]);
	};

	// Called by the draft-backed journal card once QuickBooks accepts the
	// entry. The card itself shows the full §9 result panel; this just marks
	// the message done and refreshes the Cost Allocations view (posting flips
	// the period's timesheet to Published).
	const handleJournalDraftPosted = (msgIndex: number, action: CreatePayrollJournalProposal, result: PostResult) => {
		setActionStatus(msgIndex, 'confirmed');
		dispatch(notifyDataChanged({
			scope: 'costAllocation',
			companyId: action.companyId,
			payPeriodId: result?.draft?.payPeriodId ?? action.payPeriodId,
		}));
	};

	const confirmAction = async (msgIndex: number, action: PendingAction) => {
		// Draft-backed journals are approved from JournalDraftReview's own
		// Post button (POST /journal-drafts/:id/approve), not the generic
		// confirm endpoint.
		if (isJournalDraftAction(action)) {
			return;
		}
		setActionStatus(msgIndex, 'confirming');
		try {
			const response = await postApi('/ai/actions/confirm', action);

			// Tell whichever visible table this write affects to pick up the
			// change, without ChatBubble needing to know that table's current
			// filters/sort/page.
			//
			// A mapping rule confirmed from the sentence path
			// (propose_add_gl_mapping_rule) is written straight to the DB, so the
			// Custom Mapping Rules table in the Settings drawer has no idea. The
			// builder handoff needs none of this — the Add Rule modal refreshes
			// the table itself on save.
			if (action.type === 'add_gl_mapping_rule') {
				dispatch(notifyDataChanged({
					scope: 'configurationCustomRules',
					companyId: action.companyId,
					payPeriodId: action.payPeriodId,
				}));
			}

			if (
				action.type === 'add_employee' ||
				action.type === 'update_employee_cost' ||
				action.type === 'bulk_update_employee_cost'
			) {
				dispatch(notifyDataChanged({
					scope: 'costAllocation',
					companyId: action.companyId,
					payPeriodId: action.payPeriodId,
					// Carried so the Employee Cost grid can paint the edited cell at
					// once. That grid holds its rows in local state seeded from the
					// store, so the applyEmployeeCostFieldValue dispatch below —
					// which targets the Cost Allocation store — never reaches it.
					cell:
						action.type === 'update_employee_cost'
							? {
								employeeId: action.employeeId,
								fieldId: action.fieldId,
								value: action.newValue,
							}
							: undefined,
				}));
				// A bulk change deliberately gets no optimistic local patch: the
				// proposal carries counts, not the per-employee rows, so there is
				// nothing to patch cell-by-cell. The notifyDataChanged refetch
				// above is the only path that reconciles it — which also keeps the
				// grid honest if the server-side scope resolved to a different set
				// than the count shown on the card.
				// Patch the specific cell right away — notifyDataChanged's refetch
				// above will still reconcile section totals/labor burden shortly
				// after, but the edited field shouldn't wait on that round trip.
				if (action.type === 'update_employee_cost') {
					dispatch(applyEmployeeCostFieldValue({
						employeeId: action.employeeId,
						fieldId: action.fieldId,
						value: action.newValue,
					}));
				}
			} else if (action.type === 'update_expense_line') {
				// The synthetic .fulfilled below patches the edited line row so it
				// updates instantly, but it can't recompute SummaryTable's totals —
				// those come from a separate getSummaryAction fetch and would
				// otherwise sit stale until the user changed a filter.
				dispatch(notifyDataChanged({
					scope: 'expenseAllocation',
					companyId: action.companyId,
				}));
				const ref = { value: action.newValueId, name: action.newValueName };
				dispatch(updateTransactionLineItemAction.fulfilled(
					response?.data,
					'ai-assistant-confirm',
					{
						txnId: action.txnId,
						lineId: action.lineId,
						entityType: action.entityType,
						accountRef: action.targetField === 'account' ? ref : undefined,
						customerRef: action.targetField === 'customer' ? ref : undefined,
						classRef: action.targetField === 'class' ? ref : undefined,
						locationRef: action.targetField === 'location' ? ref : undefined,
					}
				));
			} else if (
				action.type === 'update_time_activity' ||
				action.type === 'bulk_update_time_activity'
			) {
				dispatch(notifyDataChanged({
					scope: 'timeActivity',
					companyId: action.companyId,
					payPeriodId: action.payPeriodId,
				}));
			} else if (action.type === 'create_payroll_journal') {
				// Posting flips the period's timesheet to Published, which the Cost
				// Allocations page shows; the Journal Entries list refetches on open.
				dispatch(notifyDataChanged({
					scope: 'costAllocation',
					companyId: action.companyId,
					payPeriodId: action.payPeriodId,
				}));
			} else if (action.type === 'update_indirect_rate') {
				// Every salary allocation in the period is recalculated.
				dispatch(notifyDataChanged({
					scope: 'costAllocation',
					companyId: action.companyId,
					payPeriodId: action.payPeriodId,
				}));
			} else if (action.type === 'import_employee_cost') {
				// No optimistic per-cell patch (unlike update_employee_cost) — an
				// import can touch dozens of rows/columns at once, so the grid just
				// refetches via notifyDataChanged like the bulk cost write does.
				dispatch(notifyDataChanged({
					scope: 'costAllocation',
					companyId: action.companyId,
					payPeriodId: action.payPeriodId,
				}));
			}

			setActionStatus(msgIndex, 'confirmed');
			// For a bulk write, report what the server actually touched rather
			// than the proposal's projection — the two can legitimately differ if
			// the roster changed between proposing and confirming.
			const applied = response?.data?.data;
			const doneText =
				action.type === 'bulk_update_employee_cost' &&
				typeof applied?.employeesAffected === 'number'
					? `Done — ${action.fieldName} updated for ${applied.employeesAffected} employee${applied.employeesAffected === 1 ? '' : 's'}` +
						(applied.createdCells > 0
							? `, ${applied.createdCells} new row${applied.createdCells === 1 ? '' : 's'} created`
							: '') +
						// Cells the server's numeric guard left alone (e.g. time-style
						// '0:00' values). Saying nothing here would imply every target
						// changed when it didn't.
						(applied.skippedCells > 0
							? `, ${applied.skippedCells} skipped because their current value isn't a number.`
							: '.')
					: action.type === 'import_employee_cost'
						? `Done — imported ${action.request.rows.length} record${action.request.rows.length === 1 ? '' : 's'} into ${action.payPeriodLabel} (total $${action.totalAmount.toFixed(2)}): ${applied?.upsertedCosts ?? 0} cost value${applied?.upsertedCosts === 1 ? '' : 's'}` +
							(applied?.createdFields > 0 ? `, ${applied.createdFields} new field${applied.createdFields === 1 ? '' : 's'}` : '') +
							(applied?.createdEmployees > 0 ? `, ${applied.createdEmployees} new employee${applied.createdEmployees === 1 ? '' : 's'}` : '') +
							(applied?.reactivatedEmployees > 0 ? `, ${applied.reactivatedEmployees} employee${applied.reactivatedEmployees === 1 ? '' : 's'} reactivated` : '') +
							'.'
						: action.type === 'create_payroll_journal' && typeof applied?.reply === 'string' && applied.reply.trim()
							? applied.reply.trim()
						: action.type === 'update_indirect_rate'
							? `Done. The indirect rate for ${action.payPeriodLabel} is now ${action.newRate}%.` +
								(action.journalPublished ? ' Re-post the journal from Cost Allocations → Preview Journal to update QuickBooks.' : '')
						: action.type === 'sync_time_activity'
							? `Sync started for ${action.payPeriodLabel}. You'll get a notification when the time logs are in; then ask me again.`
						: action.type === 'qbo_mcp_write' && typeof applied?.reply === 'string' && applied.reply.trim()
							? `Based on QuickBooks Online data:

${applied.reply.trim()}`
							: `Done — ${action.summary}`;
			setMessages((prev) => [
				...prev,
				{ role: 'assistant', text: doneText, timestamp: Date.now() },
			]);
		} catch (err: any) {
			setActionStatus(msgIndex, 'error');
			const errorMessage =
				err?.response?.data?.message || "Something went wrong making that change.";
			toastText(errorMessage, 'error');
			setMessages((prev) => [
				...prev,
				{ role: 'assistant', text: `I couldn't make that change: ${errorMessage}`, timestamp: Date.now() },
			]);
		}
	};

	// Handles one edit in the import card's table (see renderImportTable below).
	// Unlike confirmAction, this doesn't add a new chat bubble — it just
	// updates the SAME card's pendingAction in place with the freshly
	// recomputed (and re-signed) proposal the server returns, so editing feels
	// like a spreadsheet rather than a conversation.
	const adjustImportCell = async (msgIndex: number, rowId: string, fieldKey: string, newValue: string) => {
		const action = messages[msgIndex]?.pendingAction;
		if (!action || action.type !== 'import_employee_cost') {
			return;
		}
		const companyId = localStorage.getItem('companyId') || undefined;
		try {
			const response = await postApi('/ai/actions/adjust-import', {
				companyId,
				pending: action,
				rowId,
				fieldKey,
				newValue,
			});
			const updated: ImportEmployeeCostProposal | undefined = response?.data?.data?.pendingAction;
			if (updated) {
				setMessages((prev) => prev.map((m, i) => (i === msgIndex ? { ...m, pendingAction: updated } : m)));
			}
		} catch (err: any) {
			toastText(err?.response?.data?.message || "Couldn't update that value.", 'error');
		}
	};

	// Same shape as setActionStatus, kept separate since a message could in
	// principle carry both a pendingAction and a pendingNavigation and their
	// statuses need to move independently.
	const setNavigationStatus = (msgIndex: number, status: NavigationStatus) => {
		setMessages((prev) =>
			prev.map((m, i) => (i === msgIndex ? { ...m, navigationStatus: status } : m))
		);
	};

	const cancelNavigation = (msgIndex: number) => {
		setNavigationStatus(msgIndex, 'cancelled');
		setMessages((prev) => [
			...prev,
			{ role: 'assistant', text: "Okay, staying here.", timestamp: Date.now() },
		]);
	};

	// Purely a client-side route change — no backend call needed, unlike
	// confirmAction, since this never had a pending write to re-validate.
	const startDownload = async (msgIndex: number, download: FileDownload) => {
		const setStatus = (status: DownloadStatus) =>
			setMessages((prev) => prev.map((m, i) => (i === msgIndex ? { ...m, downloadStatus: status } : m)));
		setStatus('downloading');
		try {
			await downloadReportFile(download);
			setStatus('done');
		} catch (err: any) {
			setStatus('error');
			toastText(err?.response?.data?.message || "That report couldn't be downloaded. Try again, or export it from the Reports page.", 'error');
		}
	};

	const confirmNavigation = (msgIndex: number, pendingNavigation: PendingNavigation) => {
		setNavigationStatus(msgIndex, 'confirmed');
		setMessages((prev) => [
			...prev,
			{ role: 'assistant', text: `Taking you to **${pendingNavigation.label}** now.`, timestamp: Date.now() },
		]);
		if (APP_PAGE_PATHS.has(pendingNavigation.path.split('?')[0])) {
			navigate(pendingNavigation.path);
		}
	};

	// Other pages (e.g. the Dashboard briefing strip) ask the assistant a
	// question by dispatching askAssistant() — this component lives once at
	// the layout level, so it opens itself and sends the message on request.
	useEffect(() => {
		if (nonce > 0 && pendingMessage) {
			openChat();
			sendMessage(pendingMessage);
			dispatch(clearPendingMessage());
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [nonce]);

	// "New chat" in the sidebar clears the thread and gets out of the way: it
	// navigates to the /chat page, which has its own greeting and composer, so
	// popping this panel on top would leave two composers on screen with
	// nothing in either. The panel comes back on its own when a question is
	// actually asked, via the askAssistant effect above.
	useEffect(() => {
		if (openNonce > 0) {
			// Starting a new chat doesn't abort the previous reply — it keeps running
			// (its row keeps its spinner, it saves, and gets an unread dot). Bumping
			// the token just keeps its result from bleeding into this fresh chat.
			sendSeqRef.current += 1;
			setIsSending(false);
			setMessages([]);
			setConversationId(null);
			setInput('');
			// A pinned skill belongs to the thread it was pinned in — a fresh chat
			// starts with none.
			setForcedSkill(null);
			setSkillMenuOpen(false);
			setIsOpen(false);
			setIsClosing(false);
			setIsMaximized(false);
			drag.resetPosition();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [openNonce]);

	// Switching the header company starts a fresh thread: the open conversation
	// belongs to the previous company, and chat is scoped per company. Skips the
	// first run (initial company resolve) so it only fires on an actual switch.
	const prevCompanyIdRef = useRef<string | null>(null);
	useEffect(() => {
		if (prevCompanyIdRef.current && prevCompanyIdRef.current !== activeCompanyId) {
			// Abort any in-flight reply from the previous company so its loader
			// stops and its answer can't land in the new company's fresh thread.
			// Don't abort the in-flight reply — let it finish and save (its unread
			// dot flags it). Bumping the token keeps its result out of this view.
			sendSeqRef.current += 1;
			setIsSending(false);
			setMessages([]);
			setConversationId(null);
			setInput('');
			// Skills are per-company — drop the previous company's pinned skill and
			// cached list so the new company's skills load fresh.
			setForcedSkill(null);
			setComposerSkills([]);
			setSkillMenuOpen(false);
			dispatch(setActiveConversation(null));
			// Show the switch skeleton for a moment so the change of company (and
			// its QuickBooks/MCP context) reads as a deliberate reload rather than
			// the thread just blanking.
			setSwitchingCompany(true);
			const timer = setTimeout(() => setSwitchingCompany(false), 650);
			prevCompanyIdRef.current = activeCompanyId;
			return () => clearTimeout(timer);
		}
		prevCompanyIdRef.current = activeCompanyId;
		return undefined;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeCompanyId]);

	// Reopen a saved thread the sidebar selected: fetch its transcript, rebuild
	// the message list, and remember its id so the next turn continues it. The
	// transient in-flight cards (an unfinished import/mapping step) are never
	// restored — the backend only persists a message's display extras (data
	// cards, follow-ups), not a mid-flow builder's live state — so a reopened
	// thread shows the completed conversation, not a half-open wizard.
	useEffect(() => {
		if (loadNonce > 0 && conversationToLoad) {
			// Opening another saved thread starts clean — a skill pinned in the
			// previous thread must not carry into this one.
			setForcedSkill(null);
			setSkillMenuOpen(false);
			loadConversation(conversationToLoad);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loadNonce]);


	const copyMessage = async (text: string, index: number) => {
		try {
			await navigator.clipboard.writeText(text);
			setCopiedIndex(index);
			setTimeout(() => setCopiedIndex((prev) => (prev === index ? null : prev)), 1600);
		} catch {
			toastText('Could not copy that to the clipboard.', 'error');
		}
	};

	// Scrolls the thread so a given message sits just below its top edge.
	// Computed against the scroll container rather than scrollIntoView, which
	// would also scroll the page behind it.
	const scrollToMessage = (index: number) => {
		const thread = threadRef.current;
		const el = thread?.querySelector(`[data-msg-index="${index}"]`);
		if (!thread || !el) return;
		const top =
			el.getBoundingClientRect().top - thread.getBoundingClientRect().top + thread.scrollTop;
		thread.scrollTo({ top: Math.max(0, top - 12), behavior: 'smooth' });
	};

	const scrollToLatest = () => {
		threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight, behavior: 'smooth' });
	};

	// 40px of slack so a thread resting a hair off the bottom — which happens
	// after an image loads and nudges the height — does not show the button.
	const handleThreadScroll = () => {
		const el = threadRef.current;
		if (!el) return;
		setIsAwayFromLatest(el.scrollHeight - el.scrollTop - el.clientHeight > 40);

		// Which question the reader is currently under — the last one whose top
		// has passed the upper eighth of the view. Drives the outline's marker.
		const threshold = el.getBoundingClientRect().top + el.clientHeight / 8;
		let current: number | null = null;
		el.querySelectorAll('[data-msg-index][data-msg-role="user"]').forEach((node) => {
			if (node.getBoundingClientRect().top <= threshold) {
				current = Number(node.getAttribute('data-msg-index'));
			}
		});
		setActiveQuestion(current);
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		sendMessage(input);
	};

	// Voice input lives in a hook so the docked and floating presentations —
	// and anything else with a composer — dictate identically.
	const { isListening, toggleListening } = useSpeechInput({
		onTranscript: setInput,
		onComplete: (transcript) => {
			setInput(transcript);
			composerRef.current?.focus();
		},
	});

	// Only the summary line's wording changes between the two kinds of set —
	// the starter asks aren't following up on anything yet.
	const isFollowUpSet = messages.length > 0;

	// Before the first message, prime with generic starter questions. After
	// that, only the assistant's own suggested follow-ups for its latest reply
	// are shown — so the chips track what was actually just discussed instead
	// of always offering the same fixed list.
	// The escalation card is an assistant turn with no follow-ups of its own —
	// reading it as "the last reply" would blank the chip strip, so look past it
	// to the reply it was appended after.
	const lastMessage = [...messages].reverse().find((m) => m.variant !== 'support');
	const quickAsks =
		messages.length === 0
			? QUICK_ASKS
			: lastMessage?.role === 'assistant' && lastMessage.followUps?.length
				? lastMessage.followUps
				: [];

	if (!embedded && !isOpen && !isClosing) {
		return (
			<button
				className={`${styles['pill']} ${drag.isDragging ? styles['dragging'] : ''} ${
					justArrived ? styles['pill--glow'] : ''
				} ${isSending || isListening ? styles['pill--busy'] : ''} ${
					isDraggingFile ? styles['pill--drag-target'] : ''
				}`}
				style={drag.style}
				// The pill is the CAP wordmark alone now — no visible label — so the
				// button needs its name spelled out for screen readers, and a title
				// for anyone who hovers wondering what it opens.
				aria-label="CAP Assistant"
				title={
					isDraggingFile
						? 'Drop to attach'
						: isSending
							? thinkingText
							: isListening
								? 'Listening…'
								: 'CAP Assistant — ready to help'
				}
				onPointerDown={drag.onPointerDown}
				onPointerMove={drag.onPointerMove}
				onPointerUp={drag.onPointerUp}
				onDragEnter={handleDragEnter}
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				onDrop={handleFileDrop}
				onClick={() => {
					if (drag.consumeDragClick()) {
						return;
					}
					openChat();
				}}
			>
				<span className={styles['pill__wordmark']}>
					<CapWordmark height={22} />
					<CapMark size={17} tone="light" className={styles['pill__spark']} />
				</span>
				{hasUnread && <span className={styles['pill__dot']} />}
			</button>
		);
	}

	return (
		<div
			ref={panelRef}
			className={
				embedded
					? `${styles['panel']} ${styles['panel--embedded']}`
					: `${styles['panel']} ${drag.isDragging ? styles['dragging'] : ''} ${
							isMaximized ? styles['maximized'] : ''
						} ${isClosing ? styles['panel--closing'] : ''}`
			}
			style={embedded ? undefined : drag.style}
			onDragEnter={handleDragEnter}
			onDragOver={handleDragOver}
			onDragLeave={handleDragLeave}
			onDrop={handleFileDrop}
		>
			{isDraggingFile && (
				<div className={styles['drop-overlay']}>
					<FileIcon />
					<span>Drop to attach</span>
					<span className={styles['drop-overlay__hint']}>
						A screenshot, or a CSV/XLSX payroll file to import
					</span>
				</div>
			)}
			{!embedded && (
			<div
				className={styles['panel__header']}
				onPointerDown={drag.onPointerDown}
				onPointerMove={drag.onPointerMove}
				onPointerUp={drag.onPointerUp}
			>
				<span className={styles['panel__wordmark']}>
					<CapWordmark height={18} />
					<CapMark size={14} tone="light" className={styles['panel__spark']} />
				</span>
				<div className={styles['panel__actions']}>
					<button
						className={styles['panel__maximize']}
						onClick={() => setIsMaximized((prev) => !prev)}
						title={isMaximized ? 'Restore' : 'Maximize'}
					>
						{isMaximized ? <RestoreIcon /> : <MaximizeIcon />}
					</button>
					{drag.hasCustomPosition && (
						<button
							className={styles['panel__reset']}
							onClick={drag.resetPosition}
							title="Reset to default position"
						>
							<ResetPositionIcon />
						</button>
					)}
					<button
						className={styles['panel__close']}
						onClick={closePanel}
						title="Close chat"
					>
						✕
					</button>
				</div>
			</div>
			)}

			<div className={styles['panel__thread']} ref={threadRef} onScroll={handleThreadScroll}>
				{/* The scrollbar belongs at the edge of the page, not against the
				    text. The scroll container is this outer div at full width;
				    this inner column is what holds the messages at a readable
				    measure. */}
				<div className={styles['panel__thread-inner']}>
				{(switchingCompany || loadingConversation) && (
					<div className={styles['company-switch-skeleton']} aria-hidden="true">
						<div className={styles['company-switch-skeleton__line']} style={{ width: '42%' }} />
						<div className={styles['company-switch-skeleton__line']} style={{ width: '88%' }} />
						<div className={styles['company-switch-skeleton__line']} style={{ width: '76%' }} />
						<div className={styles['company-switch-skeleton__line']} style={{ width: '60%' }} />
					</div>
				)}
				{!switchingCompany && !loadingConversation && messages.length === 0 && embedded && <Hero onAsk={sendMessage} />}
				{messages.length === 0 && !embedded && (
					<div className={styles['bubble-row']}>
						<CapMark size={20} tone="dark" />
						<div className={styles['bubble-ai']}>
							Hi{firstName ? ` ${firstName}` : ''}! Ask me about payroll or cost
							allocation and how CAP works, and I can look up your real data —
							employee costs, pay periods, allocations, time activity, journals,
							QuickBooks sync — take you to the right page, or make a change for
							you once you confirm it. Type below, tap the mic to ask out loud, or
							attach — or just drag and drop — a screenshot or a CSV/XLSX payroll
							file to import.
						</div>
					</div>
				)}
				{messages.map((m, idx) => {
					// A turn the user cut short isn't a reply, so it doesn't get a
					// reply's shape: no avatar, no speech bubble reading "Stopped." as
					// if the assistant chose to say it. Just a quiet status line
					// across the thread, with the one thing worth offering next.
					if (m.variant === 'stopped') {
						const retryIndex = retryableQuestionIndex(idx);
						return (
							<div key={idx} className={styles['stopped-notice']}>
								<span className={styles['stopped-notice__glyph']}>
									<StopCircleIcon />
								</span>
								<span className={styles['stopped-notice__label']}>
									You stopped this reply
								</span>
								{retryIndex !== -1 && (
									<button
										type="button"
										className={styles['stopped-notice__retry']}
										onClick={() => retryStopped(idx)}
										disabled={isSending}
									>
										<RetryIcon />
										Try again
									</button>
								)}
							</div>
						);
					}

					// Not a reply either — an offer. Rendered as its own card with the
					// draft overview in an editable textarea, so what gets filed is
					// what the user actually wants to say, not just what the assistant
					// inferred.
					if (m.variant === 'support') {
						return (
							<div key={idx} className={styles['support-card']}>
								<div className={styles['support-card__head']}>
									<span className={styles['support-card__glyph']}>
										<LifebuoyIcon />
									</span>
									<span className={styles['support-card__title']}>
										{m.supportStatus === 'sent'
											? 'Sent to our support team'
											: m.supportStatus === 'dismissed'
												? 'No problem — I’m still here'
												: m.supportReason === 'user_asked'
													? 'Let’s get a person on this'
													: 'Want a human to take a look?'}
									</span>
								</div>

								{m.supportStatus === 'drafting' && (
									<div className={styles['support-card__note']}>
										Summarizing the chat for our support team…
									</div>
								)}

								{(m.supportStatus === 'ready' || m.supportStatus === 'sending') && (
									<>
										<div className={styles['support-card__note']}>
											{supportIntro(m.supportReason)} Edit anything you’d like, and
											I’ll open a support ticket with the full conversation
											attached.
										</div>
										<textarea
											className={styles['support-card__editor']}
											value={m.supportSummary ?? ''}
											placeholder="Describe what you need help with…"
											rows={7}
											disabled={m.supportStatus === 'sending'}
											onChange={(e) => updateSupportCard({ supportSummary: e.target.value })}
										/>
										<div className={styles['support-card__buttons']}>
											<button
												type="button"
												className={styles['support-card__send']}
												disabled={m.supportStatus === 'sending'}
												onClick={() => sendSupportTicket(m)}
											>
												{m.supportStatus === 'sending' ? 'Sending…' : 'Send to support'}
											</button>
											<button
												type="button"
												className={styles['support-card__dismiss']}
												disabled={m.supportStatus === 'sending'}
												onClick={dismissSupportCard}
											>
												Not now
											</button>
										</div>
									</>
								)}

								{m.supportStatus === 'sent' && (
									<div className={styles['support-card__note']}>
										{m.supportTicketRef
											? `Your ticket (ref ${m.supportTicketRef}) is with our support team — they’ll reply by email.`
											: 'Your ticket is with our support team — they’ll reply by email.'}{' '}
										You can keep chatting here in the meantime.
									</div>
								)}

								{m.supportStatus === 'dismissed' && (
									<div className={styles['support-card__note']}>
										Ask me anything else — or say the word and I’ll send this over to
										support.
									</div>
								)}
							</div>
						);
					}
					const bubble = (
						<div
							className={m.role === 'user' ? styles['bubble-user'] : styles['bubble-ai']}
						>
							{m.imageUrl && (
								<img className={styles['bubble-image']} src={m.imageUrl} alt="Attachment" />
							)}
							{m.importFileName && (
								<div className={styles['attachment-row']}>
									<FileIcon />
									<span className={styles['attachment-row__name']}>{m.importFileName}</span>
								</div>
							)}
							{/* A long paste (code, a stack trace, a wall of log output) used
							    to force the whole thread to scroll sideways — the bubble
							    capped its width, but unbroken tokens overflowed it anyway.
							    This wrapper keeps text inside the bubble and gives a very
							    tall message its own vertical scroll instead of pushing the
							    composer off screen. */}
							<div className={styles['bubble-text']}>
								{m.role === 'assistant' ? renderAssistantMessage(m.text, navigate) : m.text}
								{m.streaming && (
									// A caret while the reply streams in, so the bubble reads as
									// live rather than finished-but-short.
									<span aria-hidden="true" className={styles['bubble-caret']} style={{ opacity: 0.6 }}>▍</span>
								)}
							</div>
							{m.role === 'user' && m.skillName && (
								<div className={styles['bubble-skill']} title={`Answered using the ${m.skillName} skill`}>
									<SparkIcon className={styles['bubble-skill__glyph']} />
									<span>{m.skillName}</span>
								</div>
							)}
							{m.role === 'user' && (
								<button
									type="button"
									className={styles['bubble-edit']}
									title="Edit and resend"
									aria-label="Edit and resend this question"
									onClick={() => startEditMessage(idx)}
								>
									<EditIcon />
								</button>
							)}
							{m.role === 'assistant' && renderDataCard(m.dataCard, `card-${idx}`)}
							{m.smartImportStep && <SmartImportCard step={m.smartImportStep} chat={importChat} />}
						{m.directAllocationStep && (
							<DirectAllocationCard step={m.directAllocationStep} chat={directAllocationChat} />
						)}
						{m.mappingRuleStep && (
							<MappingRuleCard step={m.mappingRuleStep} chat={mappingRuleChat} />
						)}
						{m.timeRuleStep && <TimeRuleCard step={m.timeRuleStep} chat={timeRuleChat} />}
						{m.importChoicePending && (
							<div className={styles['action-card']}>
								<div className={styles['action-card__buttons']}>
									<button
										type="button"
										className={styles['action-card__confirm']}
										disabled={isSending}
										onClick={() => chooseImportFlow('smart')}
									>
										Import Employee Cost
									</button>
									<button
										type="button"
										className={styles['action-card__confirm']}
										disabled={isSending}
										onClick={() => chooseImportFlow('directAllocation')}
									>
										Import Direct Employee Cost
									</button>
								</div>
							</div>
						)}
							{m.fileDownload && (
								<div className={styles['action-card']}>
									<div className={styles['action-card__summary']}>{m.fileDownload.label}</div>
									<div className={styles['action-card__buttons']}>
										<button
											type="button"
											className={styles['action-card__confirm']}
											disabled={m.downloadStatus === 'downloading'}
											onClick={() => startDownload(idx, m.fileDownload as FileDownload)}
										>
											{m.downloadStatus === 'downloading'
												? 'Preparing…'
												: m.downloadStatus === 'done' || m.downloadStatus === 'error'
													? 'Download again'
													: 'Download'}
										</button>
									</div>
									{m.downloadStatus === 'done' && (
										<div className={styles['action-card__status']}>✓ Downloaded</div>
									)}
								</div>
							)}
							{m.pendingAction && (
								<div className={styles['action-card']}>
									<div className={styles['action-card__summary']}>
										{renderInlineFormatting(m.pendingAction.summary, `action-${idx}`)}
									</div>
									{m.pendingAction.type === 'add_gl_mapping_rule' && (
										<div className={styles['rule-card']}>
											<div className={styles['rule-card__row']}>
												<span className={styles['rule-card__label']}>Employees</span>
												<span className={styles['rule-card__value']}>{m.pendingAction.employeeNames.join(', ')}</span>
											</div>
											<div className={styles['rule-card__row']}>
												<span className={styles['rule-card__label']}>Pay period</span>
												<span className={styles['rule-card__value']}>{m.pendingAction.payPeriodLabel}</span>
											</div>
											{/* from → to per field, so the blast radius is visible before
											    Confirm rather than described in the summary sentence. */}
											{m.pendingAction.overrides.map((override, oi) => (
												<div className={styles['rule-card__row']} key={`ovr-${idx}-${oi}`}>
													<span className={styles['rule-card__label']}>
														{override.sectionName} · {override.fieldName}
													</span>
													<span className={styles['rule-card__value']}>
														{override.defaultAccountName} → <strong>{override.accountName}</strong>
													</span>
												</div>
											))}
											{m.pendingAction.shadowedBy && (
												<div className={styles['rule-card__warning']}>
													Rule {m.pendingAction.shadowedBy.priority} “{m.pendingAction.shadowedBy.name}” already matches
													these employees and takes precedence. This rule saves last, so reorder them in Settings →
													Configurations for it to take effect.
												</div>
											)}
										</div>
									)}
									{isJournalDraftAction(m.pendingAction) && (
										<div className={styles['journal-draft-card']}>
											<JournalDraftReview
												draftId={m.pendingAction.draftId}
												companyId={m.pendingAction.companyId}
												variant="compact"
												onPosted={(result) => handleJournalDraftPosted(idx, m.pendingAction as CreatePayrollJournalProposal, result)}
											/>
										</div>
									)}
									{m.pendingAction.type === 'create_payroll_journal' && !isJournalDraftAction(m.pendingAction) && (
										<div className={styles['rule-card']}>
											<div className={styles['rule-card__row']}>
												<span className={styles['rule-card__label']}>Pay period</span>
												<span className={styles['rule-card__value']}>{m.pendingAction.payPeriodLabel}</span>
											</div>
											<div className={styles['rule-card__row']}>
												<span className={styles['rule-card__label']}>Journal date</span>
												<span className={styles['rule-card__value']}>{formatJournalDate(m.pendingAction.journalDate)}</span>
											</div>
											<div className={styles['rule-card__row']}>
												<span className={styles['rule-card__label']}>QuickBooks</span>
												<span className={styles['rule-card__value']}>
													{m.pendingAction.mode === 'update'
														? `Updates Journal Entry #${m.pendingAction.existingQboJournalNo}`
														: 'Creates a new journal entry'}
												</span>
											</div>
											<div className={styles['rule-card__row']}>
												<span className={styles['rule-card__label']}>Notes</span>
												<span className={styles['rule-card__value']}>{m.pendingAction.notes}</span>
											</div>
											{m.pendingAction.warnings.map((warning, wi) => (
												<div className={styles['rule-card__warning']} key={`jw-${idx}-${wi}`}>
													{warning}
												</div>
											))}
											<div className={styles['import-table-wrap']}>
												<table className={styles['import-table']}>
													<thead>
														<tr>
															<th>Account</th>
															<th>Debit</th>
															<th>Credit</th>
															<th>Employee</th>
															<th>Customer</th>
															<th>Class</th>
														</tr>
													</thead>
													<tbody>
														{m.pendingAction.lines.map((line, li) => (
															<tr key={`jl-${idx}-${li}`}>
																<td>
																	{line.account}
																	{line.ruleName && (
																		<span className={styles['import-table__badge']}> {line.ruleName}</span>
																	)}
																</td>
																<td>{formatJournalAmount(line.debit)}</td>
																<td>{formatJournalAmount(line.credit)}</td>
																<td>{line.employeeName}</td>
																<td>{line.customer}</td>
																<td>{line.class}</td>
															</tr>
														))}
													</tbody>
													<tfoot>
														<tr>
															<td>Total</td>
															<td>{formatJournalAmount(m.pendingAction.totalDebit)}</td>
															<td>{formatJournalAmount(m.pendingAction.totalCredit)}</td>
															<td colSpan={3} />
														</tr>
													</tfoot>
												</table>
											</div>
										</div>
									)}
									{m.pendingAction.type === 'import_employee_cost' && (
										<div className={styles['import-table-wrap']}>
											<table className={styles['import-table']}>
												<thead>
													<tr>
														<th>Employee</th>
														{m.pendingAction.fields.map((f) => (
															<th key={f.key}>{f.label}</th>
														))}
													</tr>
												</thead>
												<tbody>
													{m.pendingAction.rowDetails.map((rd) => {
														const row = (m.pendingAction as ImportEmployeeCostProposal).request.rows.find(
															(r) => r.rowId === rd.rowId
														);
														return (
															<tr key={rd.rowId}>
																<td>
																	{rd.employeeName}
																	{rd.status === 'new' && (
																		<span className={styles['import-table__badge']}> new</span>
																	)}
																</td>
																{(m.pendingAction as ImportEmployeeCostProposal).fields.map((f) => {
																	const cell = row?.values.find((v) =>
																		v.fieldRef.type === 'existing'
																			? v.fieldRef.fieldId === f.key
																			: v.fieldRef.tempId === f.key
																	);
																	return (
																		<td key={f.key}>
																			<input
																				type="number"
																				step="0.01"
																				className={styles['import-table__input']}
																				defaultValue={cell?.value ?? '0.00'}
																				disabled={m.actionStatus !== 'pending'}
																				onBlur={(e) => {
																					const next = e.target.value.trim();
																					if (next !== '' && next !== (cell?.value ?? '0.00')) {
																						adjustImportCell(idx, rd.rowId, f.key, next);
																					}
																				}}
																			/>
																		</td>
																	);
																})}
															</tr>
														);
													})}
												</tbody>
												<tfoot>
													<tr>
														<td>Total</td>
														<td colSpan={m.pendingAction.fields.length}>
															${m.pendingAction.totalAmount.toFixed(2)}
														</td>
													</tr>
												</tfoot>
											</table>
										</div>
									)}
									{m.actionStatus === 'pending' && !isJournalDraftAction(m.pendingAction) && (
										<div className={styles['action-card__buttons']}>
											<button
												type="button"
												className={styles['action-card__confirm']}
												onClick={() => confirmAction(idx, m.pendingAction as PendingAction)}
											>
												{m.pendingAction.type === 'create_payroll_journal'
													? m.pendingAction.mode === 'update'
														? 'Update in QuickBooks'
														: 'Post to QuickBooks'
													: 'Confirm'}
											</button>
											<button
												type="button"
												className={styles['action-card__cancel']}
												onClick={() => cancelAction(idx)}
											>
												Cancel
											</button>
										</div>
									)}
									{m.actionStatus === 'confirming' && (
										<div className={styles['action-card__status']}>Making that change…</div>
									)}
									{m.actionStatus === 'confirmed' && !isJournalDraftAction(m.pendingAction) && (
										<div className={styles['action-card__status']}>✓ Confirmed</div>
									)}
									{m.actionStatus === 'cancelled' && (
										<div className={styles['action-card__status']}>Cancelled</div>
									)}
									{m.actionStatus === 'superseded' && (
										<div className={styles['action-card__status']}>Replaced by the updated import below</div>
									)}
									{m.actionStatus === 'error' && (
										<div className={styles['action-card__status']}>✕ Didn&apos;t go through</div>
									)}
								</div>
							)}
							{m.pendingNavigation && (
								<div className={styles['action-card']}>
									{m.navigationStatus === 'pending' && (
										<div className={styles['action-card__buttons']}>
											<button
												type="button"
												className={styles['action-card__confirm']}
												onClick={() =>
													confirmNavigation(idx, m.pendingNavigation as PendingNavigation)
												}
											>
												Take me there
											</button>
											<button
												type="button"
												className={styles['action-card__cancel']}
												onClick={() => cancelNavigation(idx)}
											>
												Stay here
											</button>
										</div>
									)}
									{m.navigationStatus === 'confirmed' && (
										<div className={styles['action-card__status']}>✓ Navigating…</div>
									)}
									{m.navigationStatus === 'cancelled' && (
										<div className={styles['action-card__status']}>Stayed here</div>
									)}
								</div>
							)}
						</div>
					);
					// User bubbles must stay a direct flex child of panel__thread — its
					// align-self: flex-end only takes effect one level deep, so this
					// must return the bubble itself (with a key attached) rather than
					// wrap it in another element the way the assistant row below does.
					if (m.role === 'user') {
						return cloneElement(bubble, {
							key: idx,
							'data-msg-index': idx,
							'data-msg-role': 'user',
						});
					}
					return (
						<div key={idx} className={styles['bubble-row']} data-msg-index={idx}>
							<CapMark size={20} tone="dark" />
							<div className={styles['bubble-col']}>
								{bubble}
								{/* Only for answers with something worth copying — a card-only
								    message has no prose behind it. */}
								{m.text.trim().length > 0 && (
									<div className={styles['bubble-meta']}>
										<span className={styles['bubble-meta__time']}>
											{formatMessageTime(m.timestamp)}
										</span>
										<button
											type="button"
											className={styles['bubble-meta__copy']}
											title="Copy this answer"
											aria-label="Copy this answer"
											onClick={() => copyMessage(m.text, idx)}
										>
											{copiedIndex === idx ? <CheckSmallIcon /> : <CopyIcon />}
											{copiedIndex === idx ? 'Copied' : 'Copy'}
										</button>
									</div>
								)}
							</div>
						</div>
					);
				})}
				{isAwayFromLatest && messages.length > 0 && (
					<button
						type="button"
						className={styles['jump-latest']}
						onClick={scrollToLatest}
						title="Jump to the latest message"
					>
						<ArrowDownIcon />
						Latest
					</button>
				)}
				{/* The "working" loader shows while the turn is computed, but hides
				    the moment the reply starts revealing into its own bubble — so
				    the two never show at once. */}
				{isSending && !messages.some((m) => m.streaming && m.text) && (
					<div className={`${styles['bubble-row']} ${styles['bubble-row--thinking']}`}>
						<CapMark size={20} tone="dark" className={styles['thinking-mark']} />
						<div className={styles['bubble-loading']} role="status" aria-live="polite">
							<span className={styles['bubble-loading__dots']}>
								<span className={styles['bubble-loading__dot']} />
								<span className={styles['bubble-loading__dot']} />
								<span className={styles['bubble-loading__dot']} />
							</span>
							<ThinkingLabel words={THINKING_WORDS} current={thinkingText} />
						</div>
					</div>
				)}
				</div>
			</div>

			{embedded && (
				<ThreadOutline
					questions={messages
						.map((m, idx) => ({ text: m.text, idx, role: m.role, variant: m.variant }))
						.filter((m) => m.role === 'user' && !m.variant && m.text.trim().length > 0)
						.map(({ text, idx }) => ({ text, idx }))}
					activeIndex={activeQuestion}
					onJump={scrollToMessage}
				/>
			)}

			{quickAsks.length > 0 && !(embedded && messages.length === 0) && (
				// Every suggestion set — starter asks and per-reply follow-ups alike —
				// reduces to a single summary line and opens on hover (or keyboard
				// focus, via :focus-within). That keeps the strip one line tall
				// whatever it holds: the questions get their full text on as many
				// rows as they need when open, without the closed state ever
				// squeezing the thread or clipping a chip to fit.
				<div
					className={`${styles['panel__quick']} ${styles['panel__quick--collapsible']}`}
				>
					<button type="button" className={styles['panel__quick-toggle']}>
						<SparkIcon className={styles['quick-chip__icon']} />
						{quickAsks.length} suggested {isFollowUpSet ? 'follow-up' : 'question'}
						{quickAsks.length === 1 ? '' : 's'}
					</button>
					<div className={styles['panel__quick-list']}>
						{quickAsks.map((q) => (
							<div
								key={q}
								className={styles['quick-chip']}
								onClick={() => sendMessage(q)}
							>
								<SparkIcon className={styles['quick-chip__icon']} />
								{q}
							</div>
						))}
					</div>
				</div>
			)}

			{attachedImagePreviewUrl && (
				<div className={styles['attachment-row']}>
					<img
						className={styles['attachment-row__thumb']}
						src={attachedImagePreviewUrl}
						alt="Selected attachment"
					/>
					<span className={styles['attachment-row__name']}>{attachedImage?.name}</span>
					<button
						type="button"
						className={styles['attachment-row__remove']}
						onClick={removeAttachedImage}
						title="Remove attachment"
					>
						✕
					</button>
				</div>
			)}

			{attachedImportFile && (
				<div className={styles['attachment-row']}>
					<FileIcon />
					<span className={styles['attachment-row__name']}>{attachedImportFile.name}</span>
					<button
						type="button"
						className={styles['attachment-row__remove']}
						onClick={removeAttachedImportFile}
						title="Remove attachment"
					>
						✕
					</button>
				</div>
			)}

			{isListening && (
				<div className={styles['listening-row']}>
					<span className={styles['listening-row__dot']} />
					<span>Listening… tap the mic to stop, then review and send</span>
				</div>
			)}

			{forcedSkill && (
				<div className={styles['skill-pin-row']}>
					<span className={styles['skill-pin']}>
						<span className={styles['skill-pin__glyph']}>
							<SparkIcon />
						</span>
						<span className={styles['skill-pin__label']}>{forcedSkill.name}</span>
						<button
							type="button"
							className={styles['skill-pin__remove']}
							title="Stop using this skill"
							aria-label="Remove skill"
							onClick={() => setForcedSkill(null)}
						>
							×
						</button>
					</span>
				</div>
			)}

			<form className={styles['panel__input-row']} onSubmit={handleSubmit}>
				<input
					ref={fileInputRef}
					type="file"
					accept={ATTACH_ACCEPT}
					className={styles['file-input']}
					onChange={handleFileSelected}
				/>
				<div className={styles['skill-picker']}>
					<button
						type="button"
						title="Use a skill"
						aria-label="Use a skill"
						className={`${styles['skill-btn']} ${skillMenuOpen || forcedSkill ? styles['skill-btn--on'] : ''}`}
						onClick={toggleSkillMenu}
						disabled={isSending}
					>
						{skillsLoading ? <span className={styles['skill-btn__spinner']} aria-label="Loading skills" /> : <SparkIcon />}
					</button>
					{skillMenuOpen && (
						<>
							<div className={styles['skill-menu__backdrop']} onClick={() => setSkillMenuOpen(false)} />
							<div className={styles['skill-menu']} role="menu">
								<div className={styles['skill-menu__head']}>Skills</div>
								{skillsLoading && composerSkills.length === 0 ? (
									<div className={styles['skill-menu__empty']}>Loading skills…</div>
								) : composerSkills.length === 0 ? (
									<div className={styles['skill-menu__empty']}>No active skills yet.</div>
								) : (
									<div className={styles['skill-menu__list']}>
										{composerSkills.map((skill) => (
											<button
												type="button"
												key={skill.id}
												role="menuitem"
												className={`${styles['skill-menu__item']} ${forcedSkill?.id === skill.id ? styles['skill-menu__item--active'] : ''}`}
												onClick={() => pickSkill(skill)}
												title={skill.description}
											>
												<span className={styles['skill-menu__item-glyph']}>
													<SparkIcon />
												</span>
												<span className={styles['skill-menu__item-text']}>
													<span className={styles['skill-menu__item-name']}>{skill.name}</span>
													<span className={styles['skill-menu__item-desc']}>{skill.description}</span>
												</span>
											</button>
										))}
									</div>
								)}
								<button type="button" className={styles['skill-menu__manage']} onClick={openSkillsManager}>
									Manage skills…
								</button>
							</div>
						</>
					)}
				</div>
				<button
					type="button"
					title="Attach a screenshot or a CSV/XLSX payroll file to import"
					className={styles['attach-btn']}
					onClick={handleAttachClick}
					disabled={isSending}
				>
					<AttachIcon />
				</button>
				<button
					type="button"
					title={isListening ? 'Stop listening' : 'Speak a question'}
					aria-label={isListening ? 'Stop listening' : 'Speak a question'}
					className={`${styles['mic-btn']} ${isListening ? styles['mic-btn--active'] : ''}`}
					onClick={toggleListening}
				>
					{isListening ? <StopIcon /> : <MicIcon />}
				</button>
				<input
					ref={composerRef}
					className={styles['input-box']}
					placeholder="Ask, or drop a file…"
					value={input}
					onChange={(e) => setInput(e.target.value)}
					disabled={isSending}
				/>
				{isSending ? (
					<button
						type="button"
						className={styles['stop-btn']}
						title="Stop generating"
						aria-label="Stop generating"
						onClick={stopSending}
					>
						<StopIcon />
					</button>
				) : (
					<button
						type="submit"
						className={styles['send-btn']}
						disabled={!input.trim() && !attachedImage && !attachedImportFile}
					>
						↑
					</button>
				)}
			</form>
			<div className={styles['panel__disclaimer']}>
				Answers draw on your live CAP data — verify figures before publishing.
			</div>

			<SkillsManager
				open={skillsModalOpen}
				onClose={() => {
					setSkillsModalOpen(false);
					loadComposerSkills();
				}}
				companyId={localStorage.getItem('companyId')}
			/>
		</div>
	);
};

export default ChatBubble;
